import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { auth, db } from '../../lib/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import VendorShop from '../vendor-shop/VendorShop';
import ProductDisplay from './ProductDisplay';
import FavoriteButton from './FavoriteButton';
import CartButton from './CartButton';
import VerifiedBadge from '../verified/VerifiedBadge';
import './Marketplace.css';

import {
  Search,
  Filter,
  ArrowLeft,
  ChevronRight,
  Heart,
  ShoppingCart,
  Eye,
  Package,
  Star,
  CheckCircle,
  Users,
  TrendingUp,
  X,
  MapPin,
  Grid,
  Maximize2,
  Minimize2,
  ArrowRight,
  AlertCircle,
  RefreshCw,
  User as UserIcon,
  Clock,
  Store,
  Tag,
  SlidersHorizontal
} from 'lucide-react';

// ============================================================
// TYPES
// ============================================================
type ProductCondition = 'new' | 'like_new' | 'used_good' | 'used_fair' | 'for_parts' | string;

interface Product {
  id: string;
  title: string;
  price: number;
  original_price?: number;
  currency: string;
  images: string[];
  description: string;
  detailed_description?: string;
  category: string;
  subcategory?: string;
  condition: ProductCondition;
  vendor_id: string;
  vendor_name: string;
  views_count: number;
  likes_count: number;
  sales_count: number;
  inventory: number;
  is_active: boolean;
  is_promoted: boolean;
  created_at: string;
  updated_at: string;
  tags: string[];
  delivery_campus_ids?: string[];
  is_vendor_default?: boolean;
}

interface VendorProfile {
  id: string;
  vendor_id: string;
  shop_name: string;
  profile_image: string;
  cover_image: string;
  rating: number;
  followers_count: number;
  completed_trades: number;
  is_verified: boolean;
  online_status: boolean;
  user_id: string;
  bio?: string;
  description?: string;
  location?: string;
  hashtags?: string;
  vendcat?: number;
  delivery_campus_ids?: string[];
  // Additional fields from vendor_profiles schema
  likes_count?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  total_products?: number;
  sales_count?: number;
  total_sales?: number;
  average_rating?: number;
  rating_count?: number;
  logo_url?: string;
  total_revenue?: number;
  rooms?: string[];
  room_count?: number;
  pending_balance?: number;
  available_balance?: number;
  virtual_account?: string;
  balance?: number;
  total_earnings?: number;
  total_withdrawals?: number;
  last_withdrawal_date?: string;
  contact_phone?: string;
  business_email?: string;
  website?: string;
  instagram_handle?: string;
  facebook_page?: string;
  twitter_handle?: string;
  business_hours?: string;
  return_policy?: string;
  shipping_policy?: string;
  onboarding_completed?: boolean;
}

interface VendorCategory {
  id: number;
  name: string;
  logo?: string;
  bg?: string;
}

interface Campus {
  id: string;
  name: string;
  university: string;
  state: string;
  city: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  parent_id: number | null;
  description: string;
}

interface SystemNotification {
  id: string;
  message: string;
  type: string;
  icon: string;
  bg_color: string;
  text_color: string;
  is_active: boolean;
  created_at: string;
}

interface UserNotification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface FilterState {
  searchQuery: string;
  selectedState: string;
  selectedUniversity: string;
  selectedCampus: string;
  selectedCategory: string;
  selectedVendor: string;
  priceRange: [number, number];
  conditionFilter: string;
  sortBy: 'newest' | 'price_low' | 'price_high' | 'popular' | 'trending';
}

interface UserProfile {
  id: string;
  email: string;
  phone: string;
  name: string;
  role: string;
  isVerified: boolean;
  createdAt: string;
  profileImage?: string;
  bio?: string;
  location?: string;
}

interface NavigationStackItem {
  type: 'product' | 'vendor';
  id: string;
  product?: Product;
  timestamp: number;
}

interface UserFilterPreferences {
  lastActiveFilter: FilterState;
  locationPath: string[];
  updatedAt?: string;
}

type FirebaseUserProfile = {
  name?: string;
  email?: string;
  phone?: string;
  profileImage?: string;
  role?: string;
  isVerified?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

interface VendorModalFilter {
  tab: 'sponsored' | 'recommended' | 'popular' | 'following' | 'new';
  searchQuery: string;
  locationFilter: string;
  categoryFilter: number[];
}

// Local storage key for vendor modal filter persistence
const VENDOR_FILTER_STORAGE_KEY = 'gostorez_vendor_modal_filters';

const MAX_PRODUCTS = 50;
const FAST_PAINT_BUDGET_MS = 800;
const CONCURRENT_FETCHES = 5;
const CACHE_HIT_THRESHOLD = 10;
const VENDOR_MODAL_PAGE_SIZE = 10;
const VENDOR_MODAL_SCROLL_SIZE = 5;
const VENDOR_MODAL_SEARCH_DEBOUNCE_MS = 350;
const CACHE_UPDATE_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours

const DB_NAME = 'gostorez_marketplace_cache';
const DB_VERSION = 1;
const STORE_PRODUCTS = 'products';
const STORE_VENDORS = 'vendors';
const STORE_META = 'meta';

// ============================================================
// INDEXEDDB
// ============================================================
const openMarketplaceDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_PRODUCTS)) {
          db.createObjectStore(STORE_PRODUCTS, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_VENDORS)) {
          db.createObjectStore(STORE_VENDORS, { keyPath: 'vendor_id' });
        }
        if (!db.objectStoreNames.contains(STORE_META)) {
          db.createObjectStore(STORE_META, { keyPath: 'key' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
};

const idbClearStore = async (storeName: string): Promise<void> => {
  try {
    const db = await openMarketplaceDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      tx.objectStore(storeName).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return;
  }
};

const idbPutMany = async (storeName: string, items: any[]): Promise<void> => {
  if (!items.length) return;
  try {
    const db = await openMarketplaceDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      items.forEach(item => store.put(item));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return;
  }
};

const idbGetAll = async (storeName: string): Promise<any[]> => {
  try {
    const db = await openMarketplaceDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return [];
  }
};

const idbSetMeta = async (key: string, value: any): Promise<void> => {
  try {
    const db = await openMarketplaceDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_META, 'readwrite');
      tx.objectStore(STORE_META).put({ key, value });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return;
  }
};

const idbGetMeta = async (key: string): Promise<any> => {
  try {
    const db = await openMarketplaceDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_META, 'readonly');
      const req = tx.objectStore(STORE_META).get(key);
      req.onsuccess = () => resolve(req.result?.value);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return undefined;
  }
};

// ============================================================
// IMAGE HELPERS
// ============================================================
const getThumbnailUrl = (originalUrl: string, width = 400, quality = 65): string => {
  if (!originalUrl) return originalUrl;
  if (!originalUrl.includes('/storage/v1/object/public/')) return originalUrl;
  const transformed = originalUrl.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );
  const sep = transformed.includes('?') ? '&' : '?';
  return `${transformed}${sep}width=${width}&quality=${quality}&resize=cover`;
};

const getOriginalUrl = (url: string): string => {
  if (!url) return url;
  if (url.includes('/storage/v1/render/image/public/')) {
    const cleaned = url.replace('/storage/v1/render/image/public/', '/storage/v1/object/public/');
    return cleaned.split('?')[0];
  }
  return url;
};

// ============================================================
// FUZZY SEARCH HELPERS
// ============================================================
const normaliseSearchTerm = (raw: string): string => {
  let t = raw.trim().toLowerCase();
  if (t.length <= 3) return t;
  if (t.endsWith('ies')) return t.slice(0, -3) + 'y';
  if (t.endsWith('es')) return t.slice(0, -2);
  if (t.endsWith('s') && !t.endsWith('ss')) return t.slice(0, -1);
  return t;
};

const buildSearchPatterns = (raw: string): string[] => {
  const trimmed = raw.trim().toLowerCase();
  if (trimmed.length < 2) return [];
  const patterns = new Set<string>();
  patterns.add(trimmed);
  const singular = normaliseSearchTerm(trimmed);
  if (singular !== trimmed) patterns.add(singular);
  if (trimmed.endsWith('y')) patterns.add(trimmed.slice(0, -1) + 'ies');
  else if (!trimmed.endsWith('s')) patterns.add(trimmed + 's');
  return Array.from(patterns);
};

// Parse hashtags from a string like "#food#fastfood#restaurantinlagos"
const parseHashtags = (hashtagString: string | undefined | null): string[] => {
  if (!hashtagString) return [];
  return hashtagString
    .split('#')
    .map(tag => tag.trim().toLowerCase())
    .filter(tag => tag.length > 0);
};

// Check if a search term matches any hashtag
const matchesHashtags = (searchTerm: string, hashtags: string | undefined | null): boolean => {
  const tags = parseHashtags(hashtags);
  if (tags.length === 0) return false;
  const normalizedSearch = normaliseSearchTerm(searchTerm);
  return tags.some(tag => {
    const normalizedTag = normaliseSearchTerm(tag);
    return (
      normalizedTag.includes(normalizedSearch) ||
      normalizedSearch.includes(normalizedTag)
    );
  });
};

// ============================================================
// POSTGREST SAFE SEARCH TERM
// ------------------------------------------------------------
// PostgREST .or() uses commas, parentheses, and dots as syntax.
// If the user's search contains any of those, the query breaks.
// We strip them and collapse whitespace before building .or() clauses.
// ============================================================
const sanitizePostgrestTerm = (raw: string): string => {
  if (!raw) return '';
  return raw
    .replace(/[,()%*\\."'`:;|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

// ============================================================
// LOCAL FILTER PERSISTENCE HELPERS (Fix #7)
// ============================================================
const loadLocalVendorFilters = (): Partial<VendorModalFilter> | null => {
  try {
    const raw = localStorage.getItem(VENDOR_FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      tab: parsed.tab || 'sponsored',
      searchQuery: parsed.searchQuery || '',
      locationFilter: parsed.locationFilter || '',
      categoryFilter: Array.isArray(parsed.categoryFilter) ? parsed.categoryFilter : []
    };
  } catch {
    return null;
  }
};

const saveLocalVendorFilters = (filters: VendorModalFilter) => {
  try {
    localStorage.setItem(VENDOR_FILTER_STORAGE_KEY, JSON.stringify({
      tab: filters.tab,
      searchQuery: filters.searchQuery,
      locationFilter: filters.locationFilter,
      categoryFilter: filters.categoryFilter
    }));
  } catch {
    // silent
  }
};

// ============================================================
// COMPONENT
// ============================================================
const Marketplace: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [vendorProfiles, setVendorProfiles] = useState<{ [key: string]: VendorProfile }>({});
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [vendorCategories, setVendorCategories] = useState<VendorCategory[]>([]);
  const [systemNotifications, setSystemNotifications] = useState<SystemNotification[]>([]);
  const [userNotifications, setUserNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userFilterPreferences, setUserFilterPreferences] = useState<UserFilterPreferences | null>(null);

  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Product[] | null>(null);
  const [searchMode, setSearchMode] = useState(false);

  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsBarVisible, setSuggestionsBarVisible] = useState(true);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const [navStack, setNavStack] = useState<NavigationStackItem[]>([]);
  const [navStackIndex, setNavStackIndex] = useState(-1);

  const [showMarketplaceOverlay, setShowMarketplaceOverlay] = useState(false);
  const [showVendorShop, setShowVendorShop] = useState(false);
  const [showProductDisplay, setShowProductDisplay] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const [cartStatus, setCartStatus] = useState<{ [key: string]: boolean }>({});
  const [vendorShopMaximized, setVendorShopMaximized] = useState(false);
  const [productDisplayMaximized, setProductDisplayMaximized] = useState(false);

  // Vendor Modal State
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [vendorModalFilter, setVendorModalFilter] = useState<VendorModalFilter>(() => {
    const saved = loadLocalVendorFilters();
    return {
      tab: (saved?.tab as any) || 'sponsored',
      searchQuery: saved?.searchQuery || '',
      locationFilter: saved?.locationFilter || '',
      categoryFilter: saved?.categoryFilter || []
    };
  });
  const [vendorModalVendors, setVendorModalVendors] = useState<VendorProfile[]>([]);
  const [vendorModalLoading, setVendorModalLoading] = useState(false);
  const [vendorModalHasMore, setVendorModalHasMore] = useState(true);
  const [vendorModalOffset, setVendorModalOffset] = useState(0);
  const [showVendorLocationFilter, setShowVendorLocationFilter] = useState(false);
  const [showVendorCategoryFilter, setShowVendorCategoryFilter] = useState(false);

  const retryIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const vendorModalScrollRef = useRef<HTMLDivElement>(null);
  const vendorModalSearchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const [filters, setFilters] = useState<FilterState>({
    searchQuery: '',
    selectedState: '',
    selectedUniversity: '',
    selectedCampus: '',
    selectedCategory: 'all',
    selectedVendor: '',
    priceRange: [0, 500000],
    conditionFilter: 'all',
    sortBy: 'newest'
  });

  const navigate = useNavigate();
  const marketplaceRef = useRef<HTMLDivElement>(null);
  const vendorShopRef = useRef<HTMLDivElement>(null);
  const productDisplayRef = useRef<HTMLDivElement>(null);
  const filterSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasLoadedFromCacheRef = useRef(false);

  // ============================================================
  // USER SYNC
  // ============================================================
  const fetchFirebaseUserProfile = async (firebaseUid: string): Promise<FirebaseUserProfile> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', firebaseUid));
      if (userDoc.exists()) {
        const u = userDoc.data();
        return {
          name: u.name || '',
          email: u.email || '',
          phone: u.phone || '',
          profileImage: u.profileImage || '',
          role: u.role || 'user',
          isVerified: u.isVerified || false,
          createdAt: u.createdAt?.toDate?.().toISOString() || '',
          updatedAt: u.updatedAt?.toDate?.().toISOString() || ''
        };
      }
      const fu = auth.currentUser;
      return {
        name: fu?.displayName || '',
        email: fu?.email || '',
        phone: fu?.phoneNumber || '',
        profileImage: fu?.photoURL || '',
        role: 'user',
        isVerified: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    } catch {
      const fu = auth.currentUser;
      return {
        name: fu?.displayName || '',
        email: fu?.email || '',
        phone: fu?.phoneNumber || '',
        profileImage: fu?.photoURL || '',
        role: 'user',
        isVerified: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
  };

  const syncUserToSupabase = async (firebaseUid: string, p: FirebaseUserProfile) => {
    try {
      const { data: existing } = await supabase
        .from('users')
        .select('id, firebase_uid, rooms, room_count')
        .eq('firebase_uid', firebaseUid)
        .maybeSingle();

      const userData: any = {
        firebase_uid: firebaseUid,
        name: p.name || '',
        email: p.email || '',
        phone: p.phone || '',
        avatar_url: p.profileImage || '',
        user_type: 'user',
        rooms: existing?.rooms || [],
        room_count: existing?.room_count || 0,
        updated_at: new Date().toISOString(),
        last_seen: new Date().toISOString(),
        is_active: true
      };

      if (!existing) {
        await supabase.from('users').insert([{ ...userData, created_at: new Date().toISOString() }]);
      } else {
        await supabase.from('users').update(userData).eq('id', existing.id);
      }
    } catch {
      // silent
    }
  };

  const performUserSync = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const p = await fetchFirebaseUserProfile(user.uid);
      await syncUserToSupabase(user.uid, p);
    } catch {
      // silent
    }
  };

  // ============================================================
  // FILTER PREFERENCES
  // ============================================================
  const saveFilterPreferences = async (currentFilters: FilterState) => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      const locationPath: string[] = [];
      if (currentFilters.selectedState && currentFilters.selectedState !== 'show_all')
        locationPath.push(`state:${currentFilters.selectedState}`);
      if (currentFilters.selectedUniversity) locationPath.push(`university:${currentFilters.selectedUniversity}`);
      if (currentFilters.selectedCampus) locationPath.push(`campus:${currentFilters.selectedCampus}`);
      if (currentFilters.selectedCategory !== 'all') locationPath.push(`category:${currentFilters.selectedCategory}`);
      if (currentFilters.selectedVendor) locationPath.push(`vendor:${currentFilters.selectedVendor}`);

      const prefs: UserFilterPreferences = {
        lastActiveFilter: currentFilters,
        locationPath,
        updatedAt: new Date().toISOString()
      };

      await supabase
        .from('users')
        .update({ user_filter_preferences: prefs, updated_at: new Date().toISOString() })
        .eq('firebase_uid', user.uid);

      setUserFilterPreferences(prefs);
    } catch {
      // silent
    }
  };

  const loadFilterPreferences = async (): Promise<UserFilterPreferences | null> => {
    const user = auth.currentUser;
    if (!user) return null;
    try {
      const { data } = await supabase
        .from('users')
        .select('user_filter_preferences')
        .eq('firebase_uid', user.uid)
        .maybeSingle();
      if (data?.user_filter_preferences) {
        setUserFilterPreferences(data.user_filter_preferences);
        return data.user_filter_preferences;
      }
    } catch {
      // silent
    }
    return null;
  };

  const clearFilterPreferences = async () => {
    const user = auth.currentUser;
    if (!user) return;
    try {
      await supabase
        .from('users')
        .update({ user_filter_preferences: null, updated_at: new Date().toISOString() })
        .eq('firebase_uid', user.uid);
      setUserFilterPreferences(null);
    } catch {
      // silent
    }
  };

  const applySavedFilterPreferences = async (): Promise<boolean> => {
    const prefs = await loadFilterPreferences();
    if (prefs?.lastActiveFilter) {
      setFilters(prev => ({ ...prev, ...prefs.lastActiveFilter }));
      return true;
    }
    return false;
  };

  // ============================================================
  // SEARCH HISTORY + SUGGESTIONS
  // ============================================================
  const loadSuggestions = async () => {
    const user = auth.currentUser;
    if (!user) {
      setSuggestions([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('search_history')
        .select('search_term, frequency, updated_at')
        .eq('user_id', user.uid)
        .order('updated_at', { ascending: false })
        .limit(12);

      const terms = (data || []).map((d: any) => d.search_term).filter(Boolean);
      setSuggestions(terms);
    } catch {
      setSuggestions([]);
    }
  };

  const saveSearchHistory = async (term: string) => {
    const user = auth.currentUser;
    if (!user) return;
    const cleanTerm = term.trim().toLowerCase();
    if (cleanTerm.length < 2) return;

    try {
      const { data: existing } = await supabase
        .from('search_history')
        .select('id, frequency')
        .eq('user_id', user.uid)
        .eq('search_term', cleanTerm)
        .maybeSingle();

      const now = new Date().toISOString();

      if (existing) {
        await supabase
          .from('search_history')
          .update({
            frequency: (existing.frequency || 1) + 1,
            updated_at: now,
            created_at: now
          })
          .eq('id', existing.id);
      } else {
        await supabase.from('search_history').insert({
          user_id: user.uid,
          search_term: cleanTerm,
          frequency: 1,
          created_at: now,
          updated_at: now
        });
      }

      await loadSuggestions();
    } catch {
      // silent
    }
  };

  const handleSuggestionClick = (term: string) => {
    setFilters(prev => ({ ...prev, searchQuery: term }));
    setShowSuggestions(false);
    performSearch(term);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ============================================================
  // BUILD PRODUCT OBJECTS
  // ============================================================
  const buildProductObjects = async (productsData: any[]): Promise<Product[]> => {
    if (productsData.length === 0) return [];

    const productIds = productsData.map((p: any) => p.id);
    const deliveryMap: { [key: string]: string[] } = {};

    const { data: deliveryData } = await supabase
      .from('product_delivery_locations')
      .select('product_id, campus_id')
      .in('product_id', productIds);

    deliveryData?.forEach((d: any) => {
      if (!deliveryMap[d.product_id]) deliveryMap[d.product_id] = [];
      deliveryMap[d.product_id].push(d.campus_id);
    });

    const { data: categoryData } = await supabase.from('categories').select('id,name');
    const categoryMap: { [key: number]: string } = {};
    categoryData?.forEach((c: any) => {
      categoryMap[c.id] = c.name;
    });

    const { data: pcData } = await supabase
      .from('product_categories')
      .select('product_id, category_id')
      .in('product_id', productIds);

    const productCategoryMap: { [key: string]: string } = {};
    pcData?.forEach((pc: any) => {
      if (categoryMap[pc.category_id]) productCategoryMap[pc.product_id] = categoryMap[pc.category_id];
    });

    return productsData.map((product: any) => ({
      ...product,
      original_price: product.original_price || product.price,
      detailed_description: product.detailed_description || product.description,
      subcategory: product.subcategory || '',
      condition: (product.condition as ProductCondition) || 'new',
      updated_at: product.updated_at || product.created_at || new Date().toISOString(),
      tags: product.tags || [],
      specifications: product.specifications || {},
      category: productCategoryMap[product.id] || product.category || 'Uncategorized',
      delivery_campus_ids: deliveryMap[product.id] || []
    }));
  };

  // ============================================================
  // BUILD VENDOR DEFAULT PRODUCTS (Fix #5)
  // Every vendor profile serves as a default product card
  // ============================================================
  const buildVendorDefaultProducts = (vendors: VendorProfile[]): Product[] => {
    return vendors.map(vendor => ({
      id: `vendor_default_${vendor.vendor_id}`,
      title: vendor.shop_name,
      price: 0,
      original_price: 0,
      currency: 'NGN',
      images: vendor.cover_image ? [vendor.cover_image] : (vendor.profile_image ? [vendor.profile_image] : []),
      description: vendor.bio || '',
      detailed_description: vendor.bio || '',
      category: 'Vendor',
      subcategory: '',
      condition: 'new' as ProductCondition,
      vendor_id: vendor.vendor_id,
      vendor_name: vendor.shop_name,
      views_count: 0,
      likes_count: vendor.likes_count || 0,
      sales_count: vendor.completed_trades || 0,
      inventory: 1,
      is_active: vendor.is_active !== false,
      is_promoted: false,
      created_at: vendor.created_at || new Date().toISOString(),
      updated_at: vendor.updated_at || new Date().toISOString(),
      tags: parseHashtags(vendor.hashtags),
      delivery_campus_ids: vendor.delivery_campus_ids || [],
      is_vendor_default: true
    }));
  };

  // ============================================================
  // CACHE LOADERS
  // ============================================================
  const loadProductsFromCache = async () => {
    try {
      const cachedProducts = await idbGetAll(STORE_PRODUCTS);
      const cachedVendors = await idbGetAll(STORE_VENDORS);
      if (!cachedProducts.length) return null;
      return { products: cachedProducts as Product[], vendors: cachedVendors as VendorProfile[] };
    } catch {
      return null;
    }
  };

  // ============================================================
  // FAST PRODUCT FETCHER
  // ============================================================
  const fetchProductsFresh = async (
    startingCacheCount: number,
    cacheSnapshot: Product[]
  ): Promise<{ finalProducts: Product[]; reached50: boolean; failed: boolean }> => {
    const collected: any[] = [];
    let failed = false;

    const firstRowPromise = supabase
      .from('products')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false })
      .range(0, 0);

    const budgetPromise = new Promise<null>(resolve =>
      setTimeout(() => resolve(null), FAST_PAINT_BUDGET_MS)
    );

    const firstResult = await Promise.race([firstRowPromise, budgetPromise]);

    if (firstResult && 'data' in firstResult && !firstResult.error && firstResult.data?.length) {
      collected.push(firstResult.data[0]);
      try {
        const firstBuilt = await buildProductObjects([firstResult.data[0]]);
        setProducts(firstBuilt);
        setLoading(false);
      } catch {
        const raw = firstResult.data[0];
        setProducts([{ ...raw, delivery_campus_ids: [], category: raw.category || 'Uncategorized' }]);
        setLoading(false);
      }
    } else if (firstResult && 'error' in firstResult && firstResult.error) {
      failed = true;
    }

    let nextIndex = collected.length;
    let reached50 = collected.length >= MAX_PRODUCTS;

    while (!reached50 && nextIndex < MAX_PRODUCTS) {
      const batchIndices: number[] = [];
      for (let k = 0; k < CONCURRENT_FETCHES && nextIndex + k < MAX_PRODUCTS; k++) {
        batchIndices.push(nextIndex + k);
      }

      const batchResults = await Promise.all(
        batchIndices.map(i =>
          supabase
            .from('products')
            .select('*')
            .eq('is_active', true)
            .order('created_at', { ascending: false })
            .range(i, i)
        )
      );

      let gotAny = false;
      for (const r of batchResults) {
        if (r.error) {
          failed = true;
          continue;
        }
        if (r.data && r.data.length > 0) {
          collected.push(r.data[0]);
          gotAny = true;
        }
      }

      nextIndex += batchIndices.length;

      if (collected.length > startingCacheCount) {
        try {
          const built = await buildProductObjects([...collected]);
          setProducts(built);
          setLoading(false);
        } catch {
          // skip paint if enrichment failed
        }
      }

      if (!gotAny) break;
      if (collected.length >= MAX_PRODUCTS) {
        reached50 = true;
        break;
      }
    }

    let finalProducts: Product[] = cacheSnapshot;
    try {
      finalProducts = await buildProductObjects(collected);
    } catch {
      finalProducts = collected.map((p: any) => ({
        ...p,
        original_price: p.original_price || p.price,
        detailed_description: p.detailed_description || p.description,
        subcategory: p.subcategory || '',
        condition: p.condition || 'new',
        updated_at: p.updated_at || p.created_at || new Date().toISOString(),
        tags: p.tags || [],
        category: p.category || 'Uncategorized',
        delivery_campus_ids: []
      }));
    }

    if (finalProducts.length > startingCacheCount) {
      setProducts(finalProducts);
      try {
        await idbClearStore(STORE_PRODUCTS);
        await idbPutMany(STORE_PRODUCTS, finalProducts);
        await idbSetMeta('lastFetchedAt', new Date().toISOString());
      } catch {
        // cache write failure is non-fatal
      }
    }

    return {
      finalProducts,
      reached50: finalProducts.length >= MAX_PRODUCTS,
      failed
    };
  };

  // ============================================================
  // VENDORS
  // ============================================================
  const fetchVendorsFresh = async () => {
    const { data: firstChunk, error: firstError } = await supabase
      .from('vendor_profiles')
      .select('*')
      .eq('is_active', true)
      .range(0, 0);

    if (firstError) throw firstError;

    if (!firstChunk || firstChunk.length === 0) {
      setVendorProfiles({});
      await idbClearStore(STORE_VENDORS);
      return;
    }

    const allVendorsData: any[] = [firstChunk[0]];
    let currentPage = 1;
    let hasMore = true;

    while (hasMore && allVendorsData.length < 200) {
      const start = currentPage;

      const { data: chunk, error: chunkError } = await supabase
        .from('vendor_profiles')
        .select('*')
        .eq('is_active', true)
        .range(start, start);

      if (chunkError) {
        console.warn('Vendor chunk error, stopping:', chunkError.message);
        break;
      }

      if (!chunk || chunk.length === 0) {
        hasMore = false;
        break;
      }

      allVendorsData.push(chunk[0]);
      currentPage++;
      await new Promise(resolve => setTimeout(resolve, 20));
    }

    const { data: deliveryData } = await supabase
      .from('vendor_delivery_locations')
      .select('vendor_id, campus_id');

    const deliveryMap: { [key: string]: string[] } = {};
    deliveryData?.forEach((d: any) => {
      if (!deliveryMap[d.vendor_id]) deliveryMap[d.vendor_id] = [];
      deliveryMap[d.vendor_id].push(d.campus_id);
    });

    const profiles: VendorProfile[] = allVendorsData.map((profile: any) => ({
      ...profile,
      delivery_campus_ids: deliveryMap[profile.vendor_id] || [],
      rating: profile.rating || 0,
      followers_count: profile.followers_count || 0,
      completed_trades: profile.completed_trades || 0,
      is_verified: profile.is_verified || false,
      online_status: profile.online_status || false
    }));

    const map: { [key: string]: VendorProfile } = {};
    profiles.forEach(p => {
      map[p.vendor_id] = p;
    });
    setVendorProfiles(map);

    await idbClearStore(STORE_VENDORS);
    await idbPutMany(STORE_VENDORS, profiles);
  };

  const loadCampuses = async () => {
    const { data, error } = await supabase
      .from('campuses')
      .select(`
        id,
        name,
        city,
        universities (name, abbreviation),
        states (name)
      `)
      .order('states(name)');

    if (error) throw error;

    const transformed: Campus[] = (data || []).map((c: any) => ({
      id: c.id,
      name: c.name,
      university: c.universities?.name || '',
      state: c.states?.name || '',
      city: c.city
    }));
    setCampuses(transformed);
  };

  const loadCategories = async () => {
    const { data, error } = await supabase.from('categories').select('*').order('name');
    if (error) throw error;
    setCategories(data || []);
  };

  // Fix #4: Load vendor categories (vendimgcat) with id and name only
  const loadVendorCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('vendimgcat')
        .select('id, name')
        .order('name', { ascending: true });
      
      if (error) throw error;
      setVendorCategories((data || []) as VendorCategory[]);
    } catch {
      setVendorCategories([]);
    }
  };

  const loadSystemNotifications = async () => {
    const { data, error } = await supabase
      .from('system_notifications')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    if (error) throw error;
    setSystemNotifications(data || []);
  };

  const loadUserNotifications = async (userId: string) => {
    const { data } = await supabase
      .from('user_notifications')
      .select('*')
      .eq('user_id', userId)
      .eq('is_read', false)
      .order('created_at', { ascending: false });
    setUserNotifications(data || []);
  };

  // ============================================================
  // CACHE UPDATE WITH SEARCH HISTORY (Fix #4)
  // Updates cache with 20 products based on user search history
  // Runs once per 24 hours
  // ============================================================
  const updateCacheWithSearchHistory = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      // Check if we already updated today
      const lastCacheUpdate = await idbGetMeta('lastSearchCacheUpdate');
      const now = Date.now();
      
      if (lastCacheUpdate) {
        const lastUpdateTime = new Date(lastCacheUpdate).getTime();
        if (now - lastUpdateTime < CACHE_UPDATE_INTERVAL_MS) {
          return; // Already updated within 24 hours
        }
      }

      // Get user's search history
      const { data: searchHistory } = await supabase
        .from('search_history')
        .select('search_term, frequency')
        .eq('user_id', user.uid)
        .order('frequency', { ascending: false })
        .order('updated_at', { ascending: false })
        .limit(10);

      const searchTerms = (searchHistory || []).map((s: any) => s.search_term).filter(Boolean);

      let cacheProducts: Product[] = [];
      const cachedProducts = await idbGetAll(STORE_PRODUCTS);
      const cachedVendors = await idbGetAll(STORE_VENDORS);

      // 1. Products based on user's search history
      if (searchTerms.length > 0) {
        const searchPatterns: string[] = [];
        for (const term of searchTerms) {
          searchPatterns.push(...buildSearchPatterns(term));
        }

        if (searchPatterns.length > 0) {
          const orClauses = searchPatterns.map(p => 
            `title.ilike.%${sanitizePostgrestTerm(p)}%,description.ilike.%${sanitizePostgrestTerm(p)}%`
          ).join(',');

          const { data: searchedProducts } = await supabase
            .from('products')
            .select('*')
            .eq('is_active', true)
            .or(orClauses)
            .order('created_at', { ascending: false })
            .limit(10);

          if (searchedProducts) {
            const built = await buildProductObjects(searchedProducts);
            cacheProducts.push(...built);
          }
        }

        // 2. Products by same vendors (from search history)
        if (searchTerms.length > 0) {
          const vendorSearchPatterns: string[] = [];
          for (const term of searchTerms) {
            vendorSearchPatterns.push(...buildSearchPatterns(term));
          }

          if (vendorSearchPatterns.length > 0) {
            const vendorOrClauses = vendorSearchPatterns.map(p =>
              `shop_name.ilike.%${sanitizePostgrestTerm(p)}%,bio.ilike.%${sanitizePostgrestTerm(p)}%`
            ).join(',');

            const { data: matchedVendors } = await supabase
              .from('vendor_profiles')
              .select('vendor_id')
              .or(vendorOrClauses)
              .limit(10);

            if (matchedVendors && matchedVendors.length > 0) {
              const vendorIds = matchedVendors.map((v: any) => v.vendor_id);
              
              const { data: vendorProducts } = await supabase
                .from('products')
                .select('*')
                .eq('is_active', true)
                .in('vendor_id', vendorIds)
                .order('created_at', { ascending: false })
                .limit(10);

              if (vendorProducts) {
                const built = await buildProductObjects(vendorProducts);
                cacheProducts.push(...built);
              }
            }
          }
        }
      }

      // 3. Products by similar vendors (same vendcat)
      if (cachedVendors.length > 0) {
        const vendorCats = cachedVendors
          .map((v: any) => v.vendcat)
          .filter((c: any) => c != null);
        
        if (vendorCats.length > 0) {
          const uniqueCats = Array.from(new Set(vendorCats)).slice(0, 5);
          
          const { data: similarVendorProducts } = await supabase
            .from('vendor_profiles')
            .select('vendor_id')
            .in('vendcat', uniqueCats)
            .eq('is_active', true)
            .limit(10);

          if (similarVendorProducts && similarVendorProducts.length > 0) {
            const similarVendorIds = similarVendorProducts.map((v: any) => v.vendor_id);
            
            const { data: similarProducts } = await supabase
              .from('products')
              .select('*')
              .eq('is_active', true)
              .in('vendor_id', similarVendorIds)
              .order('created_at', { ascending: false })
              .limit(10);

            if (similarProducts) {
              const built = await buildProductObjects(similarProducts);
              cacheProducts.push(...built);
            }
          }
        }
      }

      // 4. Top 20 most searched products (if user hasn't searched before)
      if (cacheProducts.length < 20) {
        const { data: topSearched } = await supabase
          .from('search_history')
          .select('search_term, frequency')
          .order('frequency', { ascending: false })
          .limit(20);

        if (topSearched && topSearched.length > 0) {
          const topTerms = topSearched.map((s: any) => s.search_term);
          const topPatterns: string[] = [];
          
          for (const term of topTerms) {
            topPatterns.push(...buildSearchPatterns(term));
          }

          if (topPatterns.length > 0) {
            const orClauses = topPatterns.map(p =>
              `title.ilike.%${sanitizePostgrestTerm(p)}%,description.ilike.%${sanitizePostgrestTerm(p)}%`
            ).join(',');

            const { data: topProducts } = await supabase
              .from('products')
              .select('*')
              .eq('is_active', true)
              .or(orClauses)
              .order('views_count', { ascending: false })
              .limit(20);

            if (topProducts) {
              const built = await buildProductObjects(topProducts);
              cacheProducts.push(...built);
            }
          }
        }
      }

      // Deduplicate and limit to 20
      const seenIds = new Set<string>();
      const uniqueProducts = cacheProducts.filter(p => {
        if (seenIds.has(p.id)) return false;
        seenIds.add(p.id);
        return true;
      }).slice(0, 20);

      // Merge with existing cache (new products first)
      const existingProducts = cachedProducts.filter((p: any) => 
        !seenIds.has(p.id)
      );

      const mergedProducts = [...uniqueProducts, ...existingProducts].slice(0, MAX_PRODUCTS);

      // Save to cache
      await idbClearStore(STORE_PRODUCTS);
      await idbPutMany(STORE_PRODUCTS, mergedProducts);
      await idbSetMeta('lastSearchCacheUpdate', new Date().toISOString());

      // Update state
      setProducts(mergedProducts);

    } catch (err) {
      console.warn('Cache update with search history failed:', err);
    }
  };

  // ============================================================
  // MASTER LOAD
  // ============================================================
  const loadAllData = async (forceRefresh = false) => {
    try {
      setError(null);

      let cacheProducts: Product[] = [];
      let cacheVendors: VendorProfile[] = [];

      if (!hasLoadedFromCacheRef.current || forceRefresh) {
        const cached = await loadProductsFromCache();
        if (cached) {
          cacheProducts = cached.products;
          cacheVendors = cached.vendors;
        }
        hasLoadedFromCacheRef.current = true;
      } else {
        cacheProducts = products;
        cacheVendors = Object.values(vendorProfiles);
      }

      const cacheCount = cacheProducts.length;

      if (cacheCount > 0) {
        setProducts(cacheProducts);
        if (cacheVendors.length) {
          const vm: { [key: string]: VendorProfile } = {};
          cacheVendors.forEach(v => {
            vm[v.vendor_id] = v;
          });
          setVendorProfiles(vm);
        }
        setLoading(false);
      }

      if (cacheCount >= CACHE_HIT_THRESHOLD && !forceRefresh) {
        loadCampuses().catch(() => {});
        loadCategories().catch(() => {});
        loadVendorCategories().catch(() => {});
        loadSystemNotifications().catch(() => {});
        performUserSync().catch(() => {});
        loadSuggestions().catch(() => {});
        updateCacheWithSearchHistory().catch(() => {});
        setLoading(false);
        return;
      }

      loadCampuses().catch(() => {});
      loadCategories().catch(() => {});
      loadVendorCategories().catch(() => {});
      loadSystemNotifications().catch(() => {});
      performUserSync().catch(() => {});
      loadSuggestions().catch(() => {});
      updateCacheWithSearchHistory().catch(() => {});

      fetchProductsFresh(cacheCount, cacheProducts)
        .then(({ finalProducts }) => {
          if (finalProducts.length > cacheCount) {
            // already painted + cached inside fetchProductsFresh
          }
        })
        .catch(err => {
          console.warn('Products fetch failed, keeping cache:', err?.message);
        });

      fetchVendorsFresh().catch(err => {
        console.warn('Vendors fetch failed, keeping cache:', err?.message);
      });

      if (cacheCount === 0) {
        setTimeout(() => setLoading(false), 4000);
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      console.error('loadAllData error:', err);
      setError('Failed to load marketplace data');
      setLoading(false);
    }
  };

  // ============================================================
  // LOCATION FILTER
  // ------------------------------------------------------------
  // Products with NO delivery_campus_ids are shown in EVERY location.
  // Products that HAVE delivery_campus_ids only appear in those locations.
  // Vendor default products use vendor's location or delivery_campus_ids
  // ============================================================
  const applyLocationFilter = (input: Product[]): Product[] => {
    let out = input;

    const hasAnyLocationFilter =
      !!filters.selectedCampus ||
      !!filters.selectedUniversity ||
      (!!filters.selectedState && filters.selectedState !== 'show_all');

    if (hasAnyLocationFilter) {
      let campusIds: string[] = [];
      if (filters.selectedCampus) {
        campusIds = [filters.selectedCampus];
      } else if (filters.selectedUniversity) {
        campusIds = campuses
          .filter(c => c.university === filters.selectedUniversity)
          .map(c => c.id);
      } else {
        campusIds = campuses
          .filter(c => c.state === filters.selectedState)
          .map(c => c.id);
      }

      const selectedLocationNames = campuses
        .filter(c => campusIds.includes(c.id))
        .flatMap(c => [c.name, c.university, c.state, c.city])
        .filter(Boolean);

      out = out.filter(p => {
        // Vendor default products - check location field
        if (p.is_vendor_default) {
          const vendor = vendorProfiles[p.vendor_id];
          if (!vendor?.location) return true; // No location, show everywhere
          
          const vendorLocation = vendor.location.toLowerCase();
          return selectedLocationNames.some(loc => 
            loc && vendorLocation.includes(loc.toLowerCase())
          ) || selectedLocationNames.some(loc =>
            loc && loc.toLowerCase().includes(vendorLocation)
          );
        }

        const has = p.delivery_campus_ids || [];
        if (has.length === 0) return true;
        return has.some(id => campusIds.includes(id));
      });
    }

    if (filters.selectedCategory !== 'all') {
      const catName = categories.find(c => c.id.toString() === filters.selectedCategory)?.name;
      if (catName) {
        out = out.filter(p => {
          if (p.is_vendor_default) {
            const vendor = vendorProfiles[p.vendor_id];
            return vendor?.vendcat?.toString() === filters.selectedCategory;
          }
          return p.category === catName;
        });
      }
    }

    if (filters.selectedVendor !== '') {
      out = out.filter(p => p.vendor_id === filters.selectedVendor);
    }

    return out;
  };

  // ============================================================
  // FETCH VENDORS FOR SEARCH (Fix — same logic used by vendor modal and main search)
  // ------------------------------------------------------------
  // Any word: ilike match on shop_name, bio, hashtags.
  // NOTE: vendor_profiles has NO `description` column. Only use existing columns.
  // Returns an array of VendorProfile objects.
  // ============================================================
  const fetchVendorsForSearch = async (term: string, limit: number = 200): Promise<VendorProfile[]> => {
    const safe = sanitizePostgrestTerm(term);
    if (safe.length < 2) return [];

    const patterns = buildSearchPatterns(safe);
    const cleanedPatterns = patterns
      .map(p => sanitizePostgrestTerm(p))
      .filter(p => p.length >= 2);

    if (cleanedPatterns.length === 0) return [];

    const orClauses: string[] = [];
    for (const p of cleanedPatterns) {
      orClauses.push(
        `shop_name.ilike.%${p}%`,
        `bio.ilike.%${p}%`,
        `hashtags.ilike.%${p}%`
      );
    }

    try {
      const { data, error } = await supabase
        .from('vendor_profiles')
        .select('*')
        .eq('is_active', true)
        .or(orClauses.join(','))
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.warn('fetchVendorsForSearch error:', error.message);
        return [];
      }

      return (data || []) as VendorProfile[];
    } catch (err) {
      console.warn('fetchVendorsForSearch exception:', err);
      return [];
    }
  };

  // ============================================================
  // SEARCH — intelligent (plural/singular tolerant + hashtags + vendors)
  // Fix #8: Main search bar now also searches vendors
  // Uses exactly the same logic as vendor modal: fetch vendors by
  // shop_name/bio/hashtags, then build their default product cards.
  // ============================================================
  const performSearch = async (term: string) => {
    const q = term.trim();
    if (!q || q.length < 2) return;

    setIsSearching(true);
    setSearchMode(true);
    setShowSuggestions(false);

    saveSearchHistory(q);

    try {
      // -------- Vendors (same simple logic as vendor modal) --------
      const matchedVendors = await fetchVendorsForSearch(q, 200);

      // Post-filter hashtags in JS to be safe
      const hashtagMatchedVendors = matchedVendors.filter((v: any) =>
        matchesHashtags(q, v.hashtags) ||
        v.shop_name?.toLowerCase().includes(q.toLowerCase()) ||
        v.bio?.toLowerCase().includes(q.toLowerCase())
      );

      const matchedVendorIds = hashtagMatchedVendors.map((v: any) => v.vendor_id);

      // -------- Products --------
      let builtProducts: Product[] = [];
      try {
        const safe = sanitizePostgrestTerm(q);
        const patterns = buildSearchPatterns(safe)
          .map(p => sanitizePostgrestTerm(p))
          .filter(p => p.length >= 2);

        const productOrClauses: string[] = [];
        for (const p of patterns) {
          productOrClauses.push(`title.ilike.%${p}%`, `description.ilike.%${p}%`);
        }
        if (matchedVendorIds.length > 0) {
          productOrClauses.push(`vendor_id.in.(${matchedVendorIds.join(',')})`);
        }

        if (productOrClauses.length > 0) {
          const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('is_active', true)
            .or(productOrClauses.join(','))
            .order('created_at', { ascending: false })
            .limit(MAX_PRODUCTS);

          if (error) {
            console.warn('Product search error:', error.message);
          } else {
            builtProducts = await buildProductObjects(data || []);
          }
        }
      } catch (productErr) {
        console.warn('Product search exception:', productErr);
        builtProducts = [];
      }

      // -------- Vendor default product cards (from the fetched vendor rows) --------
      const vendorDefaultProducts = buildVendorDefaultProducts(
        hashtagMatchedVendors.map((v: any) => ({
          ...v,
          delivery_campus_ids: []
        }))
      );

      // -------- Merge + dedupe --------
      const allResults = [...vendorDefaultProducts, ...builtProducts];
      const seenIds = new Set<string>();
      const uniqueResults = allResults.filter(p => {
        if (seenIds.has(p.id)) return false;
        seenIds.add(p.id);
        return true;
      });

      setSearchResults(uniqueResults);
    } catch (err) {
      console.error('Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchMode(false);
    setSearchResults(null);
    setFilters(prev => ({ ...prev, searchQuery: '' }));
  };

  // ============================================================
  // DERIVED
  // ============================================================
  const states = useMemo(
    () => Array.from(new Set(campuses.map(c => c.state))).sort(),
    [campuses]
  );

  const universities = useMemo(() => {
    if (!filters.selectedState || filters.selectedState === 'show_all') return [];
    return Array.from(
      new Set(campuses.filter(c => c.state === filters.selectedState).map(c => c.university))
    ).sort();
  }, [campuses, filters.selectedState]);

  const filteredCampuses = useMemo(() => {
    if (!filters.selectedUniversity) return [];
    return campuses
      .filter(c => c.university === filters.selectedUniversity)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [campuses, filters.selectedUniversity]);

  // All products including vendor default products
  const allProducts = useMemo(() => {
    const vendorDefaults = buildVendorDefaultProducts(Object.values(vendorProfiles));
    const existingVendorIds = new Set(products.map(p => p.vendor_id));
    
    // Only add vendor default products for vendors that don't have regular products
    const filteredVendorDefaults = vendorDefaults.filter(
      vd => !existingVendorIds.has(vd.vendor_id)
    );
    
    return [...products, ...filteredVendorDefaults];
  }, [products, vendorProfiles]);

  const filteredProducts = useMemo(() => {
    let list = allProducts;
    list = applyLocationFilter(list);

    switch (filters.sortBy) {
      case 'price_low':
        return [...list].sort((a, b) => a.price - b.price);
      case 'price_high':
        return [...list].sort((a, b) => b.price - a.price);
      case 'popular':
        return [...list].sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
      case 'trending':
        return [...list].sort((a, b) => (b.views_count || 0) - (a.views_count || 0));
      case 'newest':
      default:
        return [...list].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
    }
  }, [allProducts, filters, campuses, categories, vendorProfiles]);

  const displayedProducts = useMemo(() => {
    const base = searchMode && searchResults !== null ? searchResults : filteredProducts;
    const filtered = searchMode ? applyLocationFilter(base) : base;

    const hasCampus = (p: Product) => (p.delivery_campus_ids?.length || 0) > 0;
    const sorted = [...filtered].sort((a, b) => {
      // Vendor default products go last
      const aIsDefault = a.is_vendor_default ? 1 : 0;
      const bIsDefault = b.is_vendor_default ? 1 : 0;
      if (aIsDefault !== bIsDefault) return aIsDefault - bIsDefault;
      
      const aHas = hasCampus(a) ? 0 : 1;
      const bHas = hasCampus(b) ? 0 : 1;
      if (aHas !== bHas) return aHas - bHas;
      return 0;
    });

    return sorted.slice(0, MAX_PRODUCTS);
  }, [searchMode, searchResults, filteredProducts, filters, campuses, categories, vendorProfiles]);

  const getVendorsForCurrentSelection = useMemo(() => {
    if (!filters.selectedCampus) return [];
    let vendorIds: string[] = [];
    if (filters.selectedCategory === 'all') {
      vendorIds = Array.from(new Set(filteredProducts.map(p => p.vendor_id)));
    } else {
      const catName = categories.find(c => c.id.toString() === filters.selectedCategory)?.name;
      if (catName) {
        vendorIds = Array.from(
          new Set(filteredProducts.filter(p => p.category === catName).map(p => p.vendor_id))
        );
      }
    }
    return vendorIds
      .map(vid => vendorProfiles[vid])
      .filter((v): v is VendorProfile => !!v)
      .sort((a, b) => a.shop_name.localeCompare(b.shop_name));
  }, [filteredProducts, filters.selectedCampus, filters.selectedCategory, vendorProfiles, categories]);

  // ============================================================
  // VENDOR MODAL FUNCTIONS
  // ------------------------------------------------------------
  // Fix #1: Sponsored tab shows only vendors whose target_id
  //         matches a real vendor_profiles.vendor_id AND status = running
  // Fix #2: Recommended tab shows all vendors with ANY badge (status = success)
  // Fix #3: Vendor search — simple ilike on shop_name, bio, hashtags
  // Fix #4: Category filter — multi-select from vendimgcat table
  // ============================================================
  const loadVendorModalVendors = async (reset: boolean = false) => {
    if (vendorModalLoading) return;
    
    setVendorModalLoading(true);
    
    try {
      const currentOffset = reset ? 0 : vendorModalOffset;
      const pageSize = reset ? VENDOR_MODAL_PAGE_SIZE : VENDOR_MODAL_SCROLL_SIZE;
      const from = currentOffset;
      const to = from + pageSize - 1;

      let query = supabase
        .from('vendor_profiles')
        .select('*')
        .eq('is_active', true);

      // Apply tab filter
      switch (vendorModalFilter.tab) {
        case 'sponsored': {
          // Fix #1: Only vendors that appear in business_ads with
          // target_type = 'vendor', status = 'running', not expired,
          // AND whose target_id actually exists in vendor_profiles.
          const { data: adVendors } = await supabase
            .from('business_ads')
            .select('target_id')
            .eq('target_type', 'vendor')
            .eq('status', 'running')
            .gte('expires_at', new Date().toISOString());
          
          if (adVendors && adVendors.length > 0) {
            const adVendorIds = adVendors
              .map((a: any) => a.target_id)
              .filter(Boolean);

            if (adVendorIds.length === 0) {
              if (reset) {
                setVendorModalVendors([]);
                setVendorModalHasMore(false);
              }
              setVendorModalLoading(false);
              return;
            }

            const { data: realVendors } = await supabase
              .from('vendor_profiles')
              .select('vendor_id')
              .in('vendor_id', adVendorIds)
              .eq('is_active', true);

            const realVendorIds = (realVendors || []).map((v: any) => v.vendor_id);

            if (realVendorIds.length === 0) {
              if (reset) {
                setVendorModalVendors([]);
                setVendorModalHasMore(false);
              }
              setVendorModalLoading(false);
              return;
            }

            query = query.in('vendor_id', realVendorIds);
          } else {
            if (reset) {
              setVendorModalVendors([]);
              setVendorModalHasMore(false);
            }
            setVendorModalLoading(false);
            return;
          }
          break;
        }
        case 'recommended': {
          // Fix #2: Any vendor with a successful badge in verified_shops.
          const { data: verifiedShops } = await supabase
            .from('verified_shops')
            .select('shop_id')
            .eq('status', 'success');

          const verifiedShopIds = (verifiedShops || [])
            .map((v: any) => v.shop_id)
            .filter(Boolean);

          if (verifiedShopIds.length === 0) {
            if (reset) {
              setVendorModalVendors([]);
              setVendorModalHasMore(false);
            }
            setVendorModalLoading(false);
            return;
          }

          query = query.in('vendor_id', verifiedShopIds);
          break;
        }
        case 'popular':
          query = query.order('followers_count', { ascending: false });
          break;
        case 'following':
          if (currentUser) {
            const { data: follows } = await supabase
              .from('vendor_follow')
              .select('vendor_id')
              .eq('user_id', currentUser.uid);
            
            if (follows && follows.length > 0) {
              const followIds = follows.map((f: any) => f.vendor_id);
              query = query.in('vendor_id', followIds);
            } else {
              setVendorModalVendors([]);
              setVendorModalHasMore(false);
              setVendorModalLoading(false);
              return;
            }
          } else {
            setVendorModalVendors([]);
            setVendorModalHasMore(false);
            setVendorModalLoading(false);
            return;
          }
          break;
        case 'new':
        default:
          query = query.order('created_at', { ascending: false });
          break;
      }

      // Fix #3: Simple vendor search — matches shop_name, bio, hashtags
      // NOTE: `description` column does NOT exist on vendor_profiles.
      if (vendorModalFilter.searchQuery.trim()) {
        const safe = sanitizePostgrestTerm(vendorModalFilter.searchQuery);
        const patterns = buildSearchPatterns(safe)
          .map(p => sanitizePostgrestTerm(p))
          .filter(p => p.length >= 2);

        if (patterns.length > 0) {
          const orClauses = patterns.map(p =>
            `shop_name.ilike.%${p}%,bio.ilike.%${p}%,hashtags.ilike.%${p}%`
          ).join(',');
          query = query.or(orClauses);
        }
      }

      // Apply location filter
      if (vendorModalFilter.locationFilter.trim()) {
        const locSafe = sanitizePostgrestTerm(vendorModalFilter.locationFilter);
        if (locSafe) {
          const locPattern = `%${locSafe}%`;
          query = query.ilike('location', locPattern);
        }
      }

      // Fix #4: Category filter — multi-select on vendcat
      if (vendorModalFilter.categoryFilter.length > 0) {
        query = query.in('vendcat', vendorModalFilter.categoryFilter);
      }

      // Apply pagination ordering for tabs that don't have custom ordering
      if (vendorModalFilter.tab !== 'popular' && vendorModalFilter.tab !== 'new') {
        query = query.order('created_at', { ascending: false });
      }

      const { data, error } = await query.range(from, to);

      if (error) throw error;

      // Get delivery locations for vendors
      const vendorIds = (data || []).map((v: any) => v.vendor_id);
      let deliveryMap: { [key: string]: string[] } = {};
      
      if (vendorIds.length > 0) {
        const { data: deliveryData } = await supabase
          .from('vendor_delivery_locations')
          .select('vendor_id, campus_id')
          .in('vendor_id', vendorIds);

        deliveryData?.forEach((d: any) => {
          if (!deliveryMap[d.vendor_id]) deliveryMap[d.vendor_id] = [];
          deliveryMap[d.vendor_id].push(d.campus_id);
        });
      }

      const vendors: VendorProfile[] = (data || []).map((v: any) => ({
        ...v,
        delivery_campus_ids: deliveryMap[v.vendor_id] || [],
        rating: v.rating || 0,
        followers_count: v.followers_count || 0,
        completed_trades: v.completed_trades || 0,
        is_verified: v.is_verified || false,
        online_status: v.online_status || false
      }));

      if (reset) {
        setVendorModalVendors(vendors);
        setVendorModalOffset(vendors.length);
      } else {
        setVendorModalVendors(prev => [...prev, ...vendors]);
        setVendorModalOffset(prev => prev + vendors.length);
      }

      setVendorModalHasMore(vendors.length === pageSize);
    } catch (err) {
      console.error('Error loading vendor modal vendors:', err);
    } finally {
      setVendorModalLoading(false);
    }
  };

  // Persist vendor modal filters locally (Fix #7)
  useEffect(() => {
    saveLocalVendorFilters(vendorModalFilter);
  }, [
    vendorModalFilter.tab,
    vendorModalFilter.searchQuery,
    vendorModalFilter.locationFilter,
    vendorModalFilter.categoryFilter
  ]);

  // Reload vendor modal on any filter/search change
  useEffect(() => {
    if (!showVendorModal) return;

    if (vendorModalSearchDebounceRef.current) {
      clearTimeout(vendorModalSearchDebounceRef.current);
      vendorModalSearchDebounceRef.current = null;
    }

    vendorModalSearchDebounceRef.current = setTimeout(() => {
      setVendorModalOffset(0);
      setVendorModalVendors([]);
      setVendorModalHasMore(true);
      loadVendorModalVendors(true);
    }, VENDOR_MODAL_SEARCH_DEBOUNCE_MS);

    return () => {
      if (vendorModalSearchDebounceRef.current) {
        clearTimeout(vendorModalSearchDebounceRef.current);
        vendorModalSearchDebounceRef.current = null;
      }
    };
  }, [
    showVendorModal,
    vendorModalFilter.tab,
    vendorModalFilter.searchQuery,
    vendorModalFilter.locationFilter,
    vendorModalFilter.categoryFilter
  ]);

  // Handle vendor modal scroll for infinite loading
  const handleVendorModalScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const bottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 100;
    
    if (bottom && vendorModalHasMore && !vendorModalLoading) {
      loadVendorModalVendors(false);
    }
  };

  // Called on Enter / Go button — commits the term and logs to search history
  const handleVendorModalSearch = () => {
    const term = vendorModalFilter.searchQuery.trim();
    if (term.length < 2) return;
    saveSearchHistory(term);
    setVendorModalOffset(0);
    setVendorModalVendors([]);
    setVendorModalHasMore(true);
    loadVendorModalVendors(true);
  };

  const clearVendorModalFilters = () => {
    setVendorModalFilter({
      tab: 'sponsored',
      searchQuery: '',
      locationFilter: '',
      categoryFilter: []
    });
    setShowVendorLocationFilter(false);
    setShowVendorCategoryFilter(false);
    try {
      localStorage.removeItem(VENDOR_FILTER_STORAGE_KEY);
    } catch {
      // silent
    }
  };

  // ============================================================
  // CART STATUS
  // ============================================================
  useEffect(() => {
    const fetchCartStatus = async () => {
      const user = auth.currentUser;
      if (!user || filteredProducts.length === 0) {
        setCartStatus({});
        return;
      }
      const ids = filteredProducts
        .filter(p => !p.is_vendor_default)
        .map(p => p.id);
      
      if (ids.length === 0) {
        setCartStatus({});
        return;
      }

      const { data } = await supabase
        .from('carts')
        .select('product_id')
        .eq('user_id', user.uid)
        .in('product_id', ids);
      const map: { [key: string]: boolean } = {};
      data?.forEach((item: any) => {
        map[item.product_id] = true;
      });
      setCartStatus(map);
    };
    fetchCartStatus();
  }, [currentUser, filteredProducts]);

  // ============================================================
  // NAV
  // ============================================================
  const addToNavStack = (item: NavigationStackItem) => {
    setNavStack(prev => {
      const s = prev.slice(0, navStackIndex + 1);
      s.push(item);
      setNavStackIndex(s.length - 1);
      return s;
    });
  };

  const handleProductClick = (product: Product) => {
    if (product.inventory === 0 && !product.is_vendor_default) return;
    addToNavStack({ type: 'product', id: product.id, product, timestamp: Date.now() });
    setShowMarketplaceOverlay(true);
    setSelectedVendorId(product.vendor_id);
    setSelectedProduct(product);
    setShowVendorShop(true);
    setTimeout(() => setShowProductDisplay(true), 100);
  };

  const handleVendorClick = (vendorId: string) => {
    addToNavStack({ type: 'vendor', id: vendorId, timestamp: Date.now() });
    setShowMarketplaceOverlay(true);
    setSelectedVendorId(vendorId);
    setSelectedProduct(null);
    setShowVendorShop(true);
    setShowProductDisplay(false);
  };

  const handleBack = () => {
    if (navStackIndex > 0) {
      const prevItem = navStack[navStackIndex - 1];
      setNavStackIndex(navStackIndex - 1);
      if (prevItem.type === 'vendor') {
        setSelectedVendorId(prevItem.id);
        setSelectedProduct(null);
        setShowVendorShop(true);
        setShowProductDisplay(false);
      } else if (prevItem.type === 'product' && prevItem.product) {
        setSelectedVendorId(prevItem.product.vendor_id);
        setSelectedProduct(prevItem.product);
        setShowVendorShop(true);
        setShowProductDisplay(true);
      }
    }
  };

  const handleForward = () => {
    if (navStackIndex < navStack.length - 1) {
      const nextItem = navStack[navStackIndex + 1];
      setNavStackIndex(navStackIndex + 1);
      if (nextItem.type === 'vendor') {
        setSelectedVendorId(nextItem.id);
        setSelectedProduct(null);
        setShowVendorShop(true);
        setShowProductDisplay(false);
      } else if (nextItem.type === 'product' && nextItem.product) {
        setSelectedVendorId(nextItem.product.vendor_id);
        setSelectedProduct(nextItem.product);
        setShowVendorShop(true);
        setShowProductDisplay(true);
      }
    }
  };

  const handleCancelStep = () => {
    if (showProductDisplay) {
      setShowProductDisplay(false);
      setProductDisplayMaximized(false);
    } else if (showVendorShop) {
      setShowVendorShop(false);
      setShowMarketplaceOverlay(false);
      setVendorShopMaximized(false);
      setSelectedVendorId(null);
      setSelectedProduct(null);
      setNavStack([]);
      setNavStackIndex(-1);
    }
  };

  const handleToggleMaximize = () => {
    if (showProductDisplay) setProductDisplayMaximized(p => !p);
    else if (showVendorShop) setVendorShopMaximized(p => !p);
  };

  const handleVendorShopProductClick = (product: Product) => {
    addToNavStack({ type: 'product', id: product.id, product, timestamp: Date.now() });
    setSelectedProduct(product);
    setShowProductDisplay(true);
  };

  const handleBackToVendorShop = () => {
    setShowProductDisplay(false);
    setProductDisplayMaximized(false);
  };

  const handleMarketplaceOverlayClick = () => {
    if (showProductDisplay) handleBackToVendorShop();
    else if (showVendorShop) handleCancelStep();
  };

  // ============================================================
  // FILTERS
  // ============================================================
  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters(prev => {
      const nf = { ...prev, [key]: value };
      if (key === 'selectedState' && (value === '' || value === 'show_all')) {
        nf.selectedUniversity = '';
        nf.selectedCampus = '';
        nf.selectedCategory = 'all';
        nf.selectedVendor = '';
      } else if (key === 'selectedUniversity' && value === '') {
        nf.selectedCampus = '';
        nf.selectedCategory = 'all';
        nf.selectedVendor = '';
      } else if (key === 'selectedCampus' && value === '') {
        nf.selectedCategory = 'all';
        nf.selectedVendor = '';
      } else if (key === 'selectedCategory' && value === 'all') {
        nf.selectedVendor = '';
      }

      if (currentUser) {
        if (filterSaveTimeoutRef.current) clearTimeout(filterSaveTimeoutRef.current);
        filterSaveTimeoutRef.current = setTimeout(() => saveFilterPreferences(nf), 1500);
      }
      return nf;
    });
  };

  const clearLocationFilters = () => {
    setFilters(prev => {
      const nf = {
        ...prev,
        selectedState: '',
        selectedUniversity: '',
        selectedCampus: '',
        selectedCategory: 'all',
        selectedVendor: ''
      };
      if (currentUser) clearFilterPreferences();
      return nf;
    });
  };

  const handleLocationBack = () => {
    if (filters.selectedVendor) updateFilter('selectedVendor', '');
    else if (filters.selectedCategory !== 'all') updateFilter('selectedCategory', 'all');
    else if (filters.selectedCampus) updateFilter('selectedCampus', '');
    else if (filters.selectedUniversity) updateFilter('selectedUniversity', '');
    else if (filters.selectedState && filters.selectedState !== 'show_all')
      updateFilter('selectedState', 'show_all');
    else if (filters.selectedState === 'show_all') updateFilter('selectedState', '');
  };

  const handlePathClick = (type: 'state' | 'university' | 'campus' | 'category' | 'vendor') => {
    switch (type) {
      case 'state':
        if (filters.selectedState === 'show_all') updateFilter('selectedState', '');
        else {
          updateFilter('selectedUniversity', '');
          updateFilter('selectedCampus', '');
          updateFilter('selectedCategory', 'all');
          updateFilter('selectedVendor', '');
        }
        break;
      case 'university':
        updateFilter('selectedCampus', '');
        updateFilter('selectedCategory', 'all');
        updateFilter('selectedVendor', '');
        break;
      case 'campus':
        updateFilter('selectedCategory', 'all');
        updateFilter('selectedVendor', '');
        break;
      case 'category':
        updateFilter('selectedVendor', '');
        break;
    }
  };

  const formatPrice = (price: number, currency = 'NGN') =>
    new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);

  const getVendorInfo = (product: Product) => {
    const v = vendorProfiles[product.vendor_id];
    return {
      name: v?.shop_name || product.vendor_name || 'Local Seller',
      logo: v?.profile_image,
      coverImage: v?.cover_image,
      rating: v?.rating || 0,
      followers: v?.followers_count || 0,
      trades: v?.completed_trades || 0,
      verified: v?.is_verified || false,
      online: v?.online_status || false,
      location: v?.location || ''
    };
  };

  // ============================================================
  // EFFECTS
  // ============================================================
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async user => {
      setCurrentUser(user);
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const d = userDoc.data();
          setUserProfile({
            id: user.uid,
            email: d.email || '',
            phone: d.phone || '',
            name: d.name || 'User',
            role: d.role || 'user',
            isVerified: d.isVerified || false,
            createdAt: d.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
            profileImage: d.profileImage || '',
            bio: d.bio || '',
            location: d.location || ''
          });
        } else {
          setUserProfile({
            id: user.uid,
            email: user.email || '',
            phone: user.phoneNumber || '',
            name: user.displayName || 'User',
            role: 'user',
            isVerified: user.emailVerified || false,
            createdAt: user.metadata.creationTime || new Date().toISOString(),
            profileImage: user.photoURL || ''
          });
        }
        loadUserNotifications(user.uid);
        await performUserSync();
        await applySavedFilterPreferences();
        await loadSuggestions();
      } else {
        setUserProfile(null);
        setSuggestions([]);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    loadAllData(false);
    return () => {
      if (retryIntervalRef.current) clearTimeout(retryIntervalRef.current);
      if (filterSaveTimeoutRef.current) clearTimeout(filterSaveTimeoutRef.current);
      if (vendorModalSearchDebounceRef.current) clearTimeout(vendorModalSearchDebounceRef.current);
    };
  }, []);

  // ============================================================
  // DIMENSIONS
  // ============================================================
  const getVendorShopDimensions = (): React.CSSProperties => {
    const navH = 33;
    return {
      position: 'fixed',
      width: vendorShopMaximized ? '100%' : '88%',
      height: `calc(${vendorShopMaximized ? '100%' : '88%'} - ${navH}px)`,
      right: 0,
      bottom: `${navH}px`,
      zIndex: 1001,
      overflow: 'hidden',
      backgroundColor: 'white',
      borderRadius: vendorShopMaximized ? '0' : '20px 0 0 0',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease'
    };
  };

  const getProductDisplayDimensions = (): React.CSSProperties => {
    const navH = 34;
    return {
      position: 'fixed',
      width: productDisplayMaximized ? '100%' : '68%',
      height: `calc(${productDisplayMaximized ? '78%' : '78%'} - ${navH}px)`,
      right: 0,
      bottom: `${navH}px`,
      zIndex: 1002,
      overflow: 'hidden',
      backgroundColor: 'white',
      borderRadius: productDisplayMaximized ? '0' : '20px 0 0 0',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s ease'
    };
  };

  // ============================================================
  // SEARCH BUTTON STATE
  // ============================================================
  const searchEnabled = filters.searchQuery.trim().length >= 2 && !isSearching;

  const handleSearchSubmit = () => {
    if (!searchEnabled) return;
    performSearch(filters.searchQuery);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchEnabled) handleSearchSubmit();
  };

  // Fix #5: Build applied filter chips for the vendor modal header
  const appliedVendorFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onClear: () => void }> = [];

    if (vendorModalFilter.locationFilter.trim()) {
      chips.push({
        key: 'location',
        label: vendorModalFilter.locationFilter.trim(),
        onClear: () => {
          setVendorModalFilter(prev => ({ ...prev, locationFilter: '' }));
        }
      });
    }

    if (vendorModalFilter.categoryFilter.length > 0) {
      const names = vendorModalFilter.categoryFilter
        .map(id => vendorCategories.find(c => c.id === id)?.name)
        .filter(Boolean)
        .join(', ');
      chips.push({
        key: 'category',
        label: names || `${vendorModalFilter.categoryFilter.length} categories`,
        onClear: () => {
          setVendorModalFilter(prev => ({ ...prev, categoryFilter: [] }));
        }
      });
    }

    if (vendorModalFilter.searchQuery.trim()) {
      chips.push({
        key: 'search',
        label: vendorModalFilter.searchQuery.trim(),
        onClear: () => {
          setVendorModalFilter(prev => ({ ...prev, searchQuery: '' }));
        }
      });
    }

    return chips;
  }, [vendorModalFilter, vendorCategories]);

  // ============================================================
  // RENDER
  // ============================================================
  if (loading && products.length === 0) {
    return (
      <div className="marketplace-loading">
        <div className="loading-spinner">
          <RefreshCw className="animate-spin" size={32} />
        </div>
        <div className="loading-text">Loading online market...</div>
      </div>
    );
  }

  return (
    <div className="marketplace-container">
      <style>{`
        .product-image-slideshow,
        .product-image-slideshow img,
        .product-image-container,
        .product-click-wrapper {
          width: 100%;
          height: 100%;
          margin: 0;
          padding: 0;
          border: 0;
          display: block;
        }
        .product-image-container {
          aspect-ratio: 1 / 1;
          overflow: hidden;
          border-radius: 8px;
          background: #f3f3f3;
          line-height: 0;
          font-size: 0;
        }
        .product-click-wrapper {
          position: relative;
          overflow: hidden;
          line-height: 0;
          font-size: 0;
        }
        .product-image-slideshow {
          display: block;
          line-height: 0;
          font-size: 0;
        }
        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          margin: 0;
          padding: 0;
          border: 0;
          vertical-align: top;
        }
        /* Fix #3: Remove blue highlight on touch/click */
        .product-card,
        .product-card *,
        .product-image-container,
        .product-click-wrapper,
        .vendor-info,
        .product-stats {
          -webkit-tap-highlight-color: transparent;
          -webkit-touch-callout: none;
          user-select: none;
        }
        .product-card:focus,
        .product-card:active,
        .product-card *:focus,
        .product-card *:active {
          outline: none !important;
          -webkit-tap-highlight-color: transparent !important;
          background-color: inherit;
        }
        /* Vendor default product card styling */
        .product-card.vendor-default-card .product-image-container {
          background: linear-gradient(135deg, rgba(155,72,25,0.1), rgba(155,72,25,0.05));
        }
        .product-card.vendor-default-card .product-image {
          object-fit: cover;
        }
        .vendor-default-badge {
          position: absolute;
          bottom: 4px;
          left: 4px;
          background: rgba(155, 72, 25, 0.9);
          color: white;
          font-size: 8px;
          padding: 2px 6px;
          border-radius: 4px;
          font-weight: 600;
          z-index: 5;
        }
        .search-action-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .search-action-btn.enabled { color: #9B4819; }
        .search-action-btn.enabled:hover { background: rgba(155,72,25,0.1); }
        .search-action-btn.disabled { color: #ccc; cursor: not-allowed; }
        .search-bar-eye-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9B4819;
          border-radius: 6px;
        }
        .search-bar-eye-btn.off { color: #bbb; }
        .search-mode-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 14px;
          background: rgba(155,72,25,0.08);
          border-bottom: 1px solid rgba(155,72,25,0.15);
          font-size: 13px;
          color: #9B4819;
          font-weight: 500;
        }
        .search-mode-banner button {
          background: none;
          border: none;
          color: #9B4819;
          font-weight: 600;
          cursor: pointer;
          padding: 4px 10px;
          border-radius: 6px;
        }
        .search-mode-banner button:hover { background: rgba(155,72,25,0.1); }
        /* Fix #6: Go search button inside search bar */
        .search-go-btn {
          position: absolute;
          right: 36px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9B4819;
          z-index: 1002;
        }
        .search-go-btn:hover { background: rgba(155,72,25,0.1); }
        .search-go-btn.disabled { color: #ccc; cursor: not-allowed; }
        .vendor-modal-button {
          background: rgba(155, 72, 25, 0.1);
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 12px;
          padding: 5px;
          width: 25px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #9B4819;
          transition: all 0.2s ease;
        }
        .vendor-modal-button:hover {
          background: rgba(155, 72, 25, 0.2);
          transform: scale(1.05);
        }
        /* Fix #2: 2 columns on mobile */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          padding: 1px;
          width: 100%;
          box-sizing: border-box;
        }
        @media (min-width: 640px) {
          .products-grid {
            grid-template-columns: repeat(3, 1fr);
          }
        }
        @media (min-width: 768px) {
          .products-grid {
            grid-template-columns: repeat(4, 1fr);
          }
        }
        @media (min-width: 1024px) {
          .products-grid {
            grid-template-columns: repeat(5, 1fr);
          }
        }
        /* Fix #1: Scrollable vendor stats before vendor stat section */
        .vendor-stats-scrollable {
          display: flex;
          gap: 10px;
          font-size: 8px;
          color: #666666;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          white-space: nowrap;
          padding: 2px 0;
        }
        .vendor-stats-scrollable::-webkit-scrollbar {
          display: none;
        }
        .vendor-location-stat {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          white-space: nowrap;
          flex-shrink: 0;
        }
        /* Product stats scrollable */
        .product-stats-scrollable {
          display: flex;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          gap: 2px;
          padding: 1px;
        }
        .product-stats-scrollable::-webkit-scrollbar {
          display: none;
        }
        /* Fix #6: Toggle filter button becomes cancel icon when open */
        .vendor-modal-filter-btn.active {
          background: #9B4819;
          color: white;
        }
        .vendor-modal-filter-btn.active:hover {
          background: #7a3914;
        }
        /* Fix #5: Header row that hosts back + title or filter chips (scrollable) */
        .vendor-modal-header {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          border-bottom: 1px solid rgba(155, 72, 25, 0.15);
          background: white;
          position: sticky;
          top: 0;
          z-index: 10;
          min-height: 44px;
          box-sizing: border-box;
        }
        .vendor-modal-back {
          background: rgba(155, 72, 25, 0.1);
          border: none;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #9B4819;
          flex-shrink: 0;
          transition: all 0.2s ease;
        }
        .vendor-modal-back:hover {
          background: rgba(155, 72, 25, 0.2);
          transform: scale(1.05);
        }
        .vendor-modal-title {
          font-size: 16px;
          font-weight: 600;
          color: #9B4819;
          margin: 0;
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        /* Filter chips inside header — fill available space, scroll horizontally */
        .vendor-modal-header-chips {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          min-width: 0;
        }
        .vendor-modal-header-chips::-webkit-scrollbar { display: none; }
        .vendor-modal-header .vendor-modal-filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 3px 8px 3px 10px;
          background: rgba(155, 72, 25, 0.1);
          border: 1px solid rgba(155, 72, 25, 0.25);
          border-radius: 12px;
          font-size: 11px;
          color: #9B4819;
          font-weight: 500;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .vendor-modal-header .vendor-modal-filter-chip button {
          background: none;
          border: none;
          cursor: pointer;
          color: #9B4819;
          padding: 1px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .vendor-modal-header .vendor-modal-filter-chip button:hover {
          background: rgba(155, 72, 25, 0.15);
        }
        /* Fix #4: Multi-select category button */
        .vendor-modal-category-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .vendor-modal-category-checkbox {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(155, 72, 25, 0.4);
          border-radius: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.15s ease;
        }
        .vendor-modal-category-option.active .vendor-modal-category-checkbox {
          background: #9B4819;
          border-color: #9B4819;
        }
        /* Fix #4: Tabs scrollable on small screens */
        .vendor-modal-tabs {
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
          flex-wrap: nowrap;
          white-space: nowrap;
        }
        .vendor-modal-tabs::-webkit-scrollbar {
          display: none;
        }
        .vendor-modal-tab {
          flex: 0 0 auto;
          min-width: max-content;
        }
      `}</style>

      <div
        ref={marketplaceRef}
        className="marketplace-content"
        style={{
          position: 'relative',
          height: '100vh',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        {/* HEADER */}
        <header className="marketplace-header">
          <div className="header-content">
            <div className="user-profile-section">
              {currentUser ? (
                (userProfile?.profileImage || currentUser.photoURL) ? (
                  <img
                    className="user-avatar"
                    src={userProfile?.profileImage || currentUser?.photoURL || ''}
                    alt="User"
                    onClick={() => navigate('/user/profile')}
                  />
                ) : (
                  <div className="user-initials-avatar" onClick={() => navigate('/user/profile')}>
                    {((name: string) => {
                      if (!name) return '?';
                      const parts = name.split(/[\s@]/);
                      return parts.slice(0, 2).map((p: string) => p[0]?.toUpperCase() || '').join('');
                    })(userProfile?.name || currentUser.displayName || currentUser.email)}
                  </div>
                )
              ) : (
                <div className="guest-avatar" onClick={() => navigate('/signin')}>
                  <UserIcon size={20} /> Login
                </div>
              )}
            </div>

            <div className="search-section">
              <div className="search-bar">
                <Search className="search-icon" size={18} />
                <input
                  className="search-input"
                  type="text"
                  placeholder="Search products, vendors..."
                  value={filters.searchQuery}
                  onChange={e => {
                    setFilters(prev => ({ ...prev, searchQuery: e.target.value }));
                    setShowSuggestions(true);
                    if (searchMode && e.target.value.trim() === '') clearSearch();
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyPress={handleSearchKeyPress}
                />
                {/* Fix #6: Go search button inside search bar */}
                <button
                  className={`search-go-btn ${searchEnabled ? '' : 'disabled'}`}
                  onClick={handleSearchSubmit}
                  disabled={!searchEnabled}
                  title="Search"
                >
                  {isSearching ? (
                    <RefreshCw className="animate-spin" size={14} />
                  ) : (
                    <ArrowRight size={14} />
                  )}
                </button>
                <button
                  className={`search-bar-eye-btn ${suggestionsBarVisible ? '' : 'off'}`}
                  onClick={() => {
                    setSuggestionsBarVisible(v => !v);
                    setShowSuggestions(v => !v);
                  }}
                  title={suggestionsBarVisible ? 'Hide suggestions' : 'Show suggestions'}
                >
                  <Eye size={18} />
                </button>
              </div>
            </div>

            <div className="header-actions">
            
              <button
                className="notification-button"
                onClick={() => setShowSortMenu(!showSortMenu)}
                title="Sort & Filter"
              >
                <Filter size={18} />
              </button>


               {/* Fix #6: Vendor icon button in place of go search button */}
              <button
                className="notification-button"
                onClick={() => setShowVendorModal(true)}
                title="Browse Vendors"
              >
                <Store size={18} />
              </button>
            </div>
          </div>
        </header>

        {/* SUGGESTIONS BAR */}
        {showSuggestions && suggestionsBarVisible && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            style={{
              position: 'fixed',
              top: '39px',
              left: 0,
              right: 0,
              background: '#f0f0f0e5',
              borderBottom: '1px solid rgba(155,73,25,0.2)',
              padding: '4px 6px',
              minHeight: '30px',
              zIndex: 9999,
              boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
              whiteSpace: 'nowrap'
            }}
          >
            <div style={{ display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
              <Clock size={12} style={{ color: '#9B4819', flexShrink: 0 }} />
              {suggestions.map((term, idx) => (
                <button
                  key={`${term}_${idx}`}
                  onClick={() => handleSuggestionClick(term)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '3px 10px',
                    background: 'rgba(155,72,25,0.08)',
                    border: '1px solid rgba(155,72,25,0.2)',
                    borderRadius: '15px',
                    fontSize: '11px',
                    color: '#333',
                    whiteSpace: 'nowrap',
                    cursor: 'pointer',
                    height: '22px',
                    flexShrink: 0
                  }}
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* SEARCH MODE BANNER */}
        {searchMode && (
          <div className="search-mode-banner">
            <span>
              {isSearching ? 'Searching...' : `Results for "${filters.searchQuery}"`}
            </span>
            <button onClick={clearSearch}>Clear</button>
          </div>
        )}

        {/* SORT MENU */}
        {showSortMenu && (
          <div className="dropdown-overlay" onClick={() => setShowSortMenu(false)}>
            <div className="sort-dropdown" onClick={e => e.stopPropagation()}>
              <h4 className="dropdown-title">Sort By</h4>
              {[
                { value: 'newest', label: 'Newest First' },
                { value: 'price_low', label: 'Price: Low to High' },
                { value: 'price_high', label: 'Price: High to Low' },
                { value: 'popular', label: 'Most Popular' },
                { value: 'trending', label: 'Trending' }
              ].map(o => (
                <button
                  key={o.value}
                  className={`sort-option ${filters.sortBy === o.value ? 'sort-option-active' : ''}`}
                  onClick={() => {
                    updateFilter('sortBy', o.value as any);
                    setShowSortMenu(false);
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* LOCATION FILTER BAR */}
        <div className="location-filter-bar">
          <div className="location-filters">
            {!filters.selectedState && !filters.selectedUniversity && !filters.selectedCampus ? (
              <div className="location-initial-state">
                <span className="welcome-title">Campus Gostorez Marketplace</span>
                <button
                  className="start-filtering-button"
                  onClick={() => updateFilter('selectedState', 'show_all')}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '21px' }}
                >
                  <MapPin size={16} />
                  Filter Location
                </button>
              </div>
            ) : filters.selectedState === 'show_all' ? (
              <div className="location-filter-header">
                <button
                  className="location-back-button"
                  onClick={() => updateFilter('selectedState', '')}
                >
                  <ArrowLeft size={16} />
                </button>
                <div className="location-current-selection">Select State</div>
                <div className="location-options-scroll">
                  {states.map(state => (
                    <button
                      key={state}
                      className="location-option-button"
                      onClick={() => updateFilter('selectedState', state)}
                    >
                      <MapPin size={14} />
                      {state}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="location-filter-header">
                <button className="location-back-button" onClick={handleLocationBack}>
                  <ArrowLeft size={16} />
                </button>
                <div className="location-path-scroll">
                  {filters.selectedState && filters.selectedState !== 'show_all' && (
                    <span
                      className="location-path-item active"
                      onClick={() => handlePathClick('state')}
                    >
                      {filters.selectedState}
                    </span>
                  )}
                  {filters.selectedUniversity && (
                    <>
                      <ChevronRight className="location-path-separator" size={12} />
                      <span
                        className="location-path-item active"
                        onClick={() => handlePathClick('university')}
                      >
                        {filters.selectedUniversity}
                      </span>
                    </>
                  )}
                  {filters.selectedCampus && (
                    <>
                      <ChevronRight className="location-path-separator" size={12} />
                      <span
                        className="location-path-item active"
                        onClick={() => handlePathClick('campus')}
                      >
                        {campuses.find(c => c.id === filters.selectedCampus)?.name ||
                          filters.selectedCampus}
                      </span>
                    </>
                  )}
                  {filters.selectedCategory !== 'all' && (
                    <>
                      <ChevronRight className="location-path-separator" size={12} />
                      <span
                        className="location-path-item active"
                        onClick={() => handlePathClick('category')}
                      >
                        {categories.find(c => c.id.toString() === filters.selectedCategory)?.name ||
                          'Category'}
                      </span>
                    </>
                  )}
                  {filters.selectedVendor !== '' && (
                    <>
                      <ChevronRight className="location-path-separator" size={12} />
                      <span
                        className="location-path-item active"
                        onClick={() => handlePathClick('vendor')}
                      >
                        {vendorProfiles[filters.selectedVendor]?.shop_name || 'Vendor'}
                      </span>
                    </>
                  )}
                </div>
                <div className="location-options-scroll">
                  {filters.selectedState &&
                    filters.selectedState !== 'show_all' &&
                    !filters.selectedUniversity &&
                    !filters.selectedCampus &&
                    universities.map(u => (
                      <button
                        key={u}
                        className="location-option-button"
                        onClick={() => updateFilter('selectedUniversity', u)}
                      >
                        <MapPin size={14} />
                        {u}
                      </button>
                    ))}
                  {filters.selectedUniversity &&
                    !filters.selectedCampus &&
                    filteredCampuses.map(c => (
                      <button
                        key={c.id}
                        className="location-option-button"
                        onClick={() => updateFilter('selectedCampus', c.id)}
                      >
                        <MapPin size={14} />
                        {c.name}
                      </button>
                    ))}
                  {filters.selectedCampus &&
                    filters.selectedCategory === 'all' &&
                    filters.selectedVendor === '' && (
                      <>
                        <button
                          className="location-option-button all-categories"
                          onClick={() => updateFilter('selectedCategory', 'all')}
                        >
                          <Grid size={14} />
                          All Categories
                        </button>
                        {categories.map(category => (
                          <button
                            key={category.id}
                            className="location-option-button"
                            onClick={() =>
                              updateFilter('selectedCategory', category.id.toString())
                            }
                          >
                            {category.name}
                          </button>
                        ))}
                      </>
                    )}
                  {filters.selectedCampus &&
                    filters.selectedCategory !== 'all' &&
                    filters.selectedVendor === '' &&
                    getVendorsForCurrentSelection.map(vendor => (
                      <button
                        key={vendor.id}
                        className="location-option-button"
                        onClick={() => updateFilter('selectedVendor', vendor.vendor_id)}
                      >
                        <ShoppingCart size={14} />
                        <span style={{ visibility: 'hidden' }}>_</span>
                        {vendor.shop_name}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* PRODUCTS */}
        <main className="products-main">
          <div className="products-grid">
            {displayedProducts.map(product => {
              const vendor = getVendorInfo(product);
              const isLowStock = !product.is_vendor_default && product.inventory > 0 && product.inventory < 10;
              const isOutOfStock = !product.is_vendor_default && product.inventory === 0;

              const originalImg =
                product.images && product.images[0] ? product.images[0] : '';
              const fallbackImg =
                vendor.coverImage || vendor.logo || '/placeholder-product.jpg';
              const thumbSrc = originalImg
                ? getThumbnailUrl(originalImg, 400, 65)
                : getThumbnailUrl(fallbackImg, 400, 65);

              const originalVendorLogo = vendor.logo || '';
              const vendorLogoSrc = originalVendorLogo
                ? getThumbnailUrl(originalVendorLogo, 80, 70)
                : '';

              return (
                <div
                  key={product.id}
                  className={`product-card ${isOutOfStock ? 'product-card-out-of-stock' : ''} ${product.is_vendor_default ? 'vendor-default-card' : ''}`}
                >
                  <div
                    className="product-image-container"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!product.is_vendor_default && product.inventory === 0) return;
                      handleProductClick(product);
                    }}
                    style={{ cursor: (!product.is_vendor_default && product.inventory === 0) ? 'not-allowed' : 'pointer' }}
                  >
                    <div className="product-click-wrapper">
                      <span className="product-image-slideshow">
                        <img
                          className="product-image"
                          src={thumbSrc}
                          alt={product.title}
                          loading="lazy"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={e => {
                            const img = e.currentTarget;
                            if (img.dataset.fallbackDone === 'true') return;
                            img.dataset.fallbackDone = 'true';
                            if (originalImg) {
                              img.src = getOriginalUrl(originalImg);
                            } else if (vendor.coverImage) {
                              img.src = getOriginalUrl(vendor.coverImage);
                            } else if (vendor.logo) {
                              img.src = getOriginalUrl(vendor.logo);
                            } else {
                              img.src = '/placeholder-product.jpg';
                            }
                          }}
                        />
                      </span>

                      {/* Vendor default badge */}
                      {product.is_vendor_default && (
                        <div className="vendor-default-badge">
                          <Store size={8} style={{ marginRight: '2px' }} />
                         
                        </div>
                      )}

                      <div className="product-floating-buttons">
                        {!product.is_vendor_default && (
                          <>
                            <FavoriteButton
                              productId={product.id}
                              className="product-floating-favorite"
                              size="sm"
                              showCount={false}
                              onLikeChange={(isLiked, newCount) => {
                                setProducts(prev =>
                                  prev.map(p =>
                                    p.id === product.id ? { ...p, likes_count: newCount } : p
                                  )
                                );
                              }}
                            />
                            <div className="product-floating-cart-wrapper">
                              <CartButton
                                productId={product.id}
                                vendorId={product.vendor_id}
                                productName={product.title}
                                className="product-floating-cart"
                                onCartUpdate={isInCart => {
                                  setCartStatus(prev => ({ ...prev, [product.id]: isInCart }));
                                }}
                              >
                                <div
                                  className="product-cart-button-inner"
                                  style={{
                                    backgroundColor: cartStatus[product.id]
                                      ? '#3fff8628'
                                      : '#ffffff0a',
                                    borderColor: cartStatus[product.id] ? '#22c55e' : '#e5e7eb',
                                    borderRadius: '50%'
                                  }}
                                >
                                  <ShoppingCart
                                    size={16}
                                    style={{ color: cartStatus[product.id] ? '#22c55e' : '#6b7280' }}
                                  />
                                </div>
                              </CartButton>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="product-badges">
                        {isOutOfStock && (
                          <div className="badge badge-out-of-stock">
                            <X size={12} /> Out of Stock
                          </div>
                        )}
                        {isLowStock && (
                          <div className="badge badge-low-stock">
                            <AlertCircle size={12} /> Low Stock
                          </div>
                        )}
                        {product.is_promoted && (
                          <div className="badge badge-promoted">
                            <TrendingUp size={12} /> Promoted
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="product-details">
                    <div className="product-info">
                      <p className="product-title">{product.title}</p>
                      <p className="product-description">
                        {product.description?.length > 80
                          ? `${product.description.substring(0, 80)}...`
                          : product.description}
                      </p>
                      {!product.is_vendor_default && (
                        <div className="product-price">
                          {formatPrice(product.price, product.currency)}
                        </div>
                      )}
                    </div>

                    <div
                      className="vendor-info"
                      onClick={() => handleVendorClick(product.vendor_id)}
                    >
                      <div className="vendor-avatar-container">
                        {vendorLogoSrc ? (
                          <img
                            className="vendor-avatar"
                            src={vendorLogoSrc}
                            alt={vendor.name}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            crossOrigin="anonymous"
                            onError={e => {
                              const img = e.currentTarget;
                              if (img.dataset.fallbackDone === 'true') return;
                              img.dataset.fallbackDone = 'true';
                              if (originalVendorLogo) {
                                img.src = getOriginalUrl(originalVendorLogo);
                              }
                            }}
                          />
                        ) : (
                          <div className="vendor-avatar-placeholder">
                            {vendor.name.charAt(0)}
                          </div>
                        )}
                        {vendor.online && <div className="online-indicator"></div>}
                      </div>
                      <div className="vendor-details">
                        <div className="vendor-name">
                          {vendor.name}
                          {vendor.verified && (
                            <CheckCircle size={10} className="verified-badge" />
                          )}
                        </div>
                        {/* Fix #1: Vendor location displays before vendor stats, scrollable */}
                        <div className="vendor-stats-scrollable">
                          {vendor.location && (
                            <span className="vendor-location-stat">
                              <MapPin size={10} /> {vendor.location}
                            </span>
                          )}
                          <span className="vendor-stat">
                            <Star size={10} /> {vendor.rating.toFixed(1)}
                          </span>
                          <span className="vendor-stat">
                            <Users size={10} /> {vendor.followers}
                          </span>
                          <span className="vendor-stat">
                            <TrendingUp size={10} /> {vendor.trades}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="product-stats-scrollable">
                      <VerifiedBadge
                        shopId={product.vendor_id}
                        variant="badge"
                        className="marketplace-vendor-verified-badge"
                      />
                      {!product.is_vendor_default && (
                        <>
                          <div className="product-stat">
                            <Eye size={12} className="stat-icon" />
                            <span className="stat-value">{product.views_count || 0}</span>
                          </div>
                          <div className="product-stat">
                            <Heart size={12} className="stat-icon" />
                            <span className="stat-value">{product.likes_count || 0}</span>
                          </div>
                          <div className="product-stat">
                            <ShoppingCart size={14} className="stat-icon" />
                            <span className="stat-value">{product.sales_count || 0}</span>
                          </div>
                          <div className="product-stat">
                            <Package size={14} className="stat-icon" />
                            <span className="stat-value">{product.inventory}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {displayedProducts.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">
                <Search size={48} />
              </div>
              <h3 className="empty-state-title">
                {searchMode
                  ? `No results for "${filters.searchQuery}"`
                  : 'No products found in this location'}
              </h3>
              <p className="empty-state-message">
                {searchMode
                  ? 'Try a different search term or clear the search'
                  : 'Wait or try adjusting your search or filters'}
              </p>
              <button
                className="clear-filters-button"
                onClick={() => {
                  if (searchMode) {
                    clearSearch();
                  } else {
                    setFilters({
                      searchQuery: '',
                      selectedState: '',
                      selectedUniversity: '',
                      selectedCampus: '',
                      selectedCategory: 'all',
                      selectedVendor: '',
                      priceRange: [0, 500000],
                      conditionFilter: 'all',
                      sortBy: 'newest'
                    });
                    clearFilterPreferences();
                  }
                }}
                style={{
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <X size={16} />
                {searchMode ? 'Clear Search' : 'Clear All Filters'}
              </button>
            </div>
          )}
        </main>
      </div>

      {/* VENDOR MODAL (Fix #6) */}
      {showVendorModal && (
        <div className="vendor-modal-overlay">
          <div className="vendor-modal">
            {/* Header — Fix #3: back icon at left, title or filter chips at right.
                No close/cancel icon on the right. */}
            <div className="vendor-modal-header">
              <button
                className="vendor-modal-back"
                onClick={() => setShowVendorModal(false)}
                title="Back"
              >
                <ArrowLeft size={20} />
              </button>
              {appliedVendorFilterChips.length === 0 ? (
                <h2 className="vendor-modal-title">My Vendors</h2>
              ) : (
                <div className="vendor-modal-header-chips">
                  {appliedVendorFilterChips.map(chip => (
                    <span key={chip.key} className="vendor-modal-filter-chip">
                      {chip.label}
                      <button
                        onClick={chip.onClear}
                        title="Clear this filter"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Search Bar — input bound directly to filter.searchQuery,
                same pattern as main product search bar */}
            <div className="vendor-modal-search-bar">
              <Search size={16} className="vendor-modal-search-icon" />
              <input
                type="text"
                placeholder="Search vendors, hashtags..."
                value={vendorModalFilter.searchQuery}
                onChange={e => setVendorModalFilter(prev => ({
                  ...prev,
                  searchQuery: e.target.value
                }))}
                onKeyPress={e => {
                  if (e.key === 'Enter') handleVendorModalSearch();
                }}
              />
              <button
                className="vendor-modal-go-btn"
                onClick={handleVendorModalSearch}
                title="Search"
              >
                <ArrowRight size={14} />
              </button>
              {/* Fix #6: toggled filter buttons show X when open */}
              <button
                className={`vendor-modal-filter-btn ${showVendorLocationFilter ? 'active' : ''}`}
                onClick={() => setShowVendorLocationFilter(!showVendorLocationFilter)}
                title={showVendorLocationFilter ? 'Close location filter' : 'Location Filter'}
              >
                {showVendorLocationFilter ? <X size={14} /> : <MapPin size={14} />}
              </button>
              <button
                className={`vendor-modal-filter-btn ${showVendorCategoryFilter ? 'active' : ''}`}
                onClick={() => setShowVendorCategoryFilter(!showVendorCategoryFilter)}
                title={showVendorCategoryFilter ? 'Close category filter' : 'Category Filter'}
              >
                {showVendorCategoryFilter ? <X size={14} /> : <SlidersHorizontal size={14} />}
              </button>
            </div>

            {/* Location Filter Dropdown */}
            {showVendorLocationFilter && (
              <div className="vendor-modal-filter-dropdown">
                <input
                  type="text"
                  placeholder="Filter by location..."
                  value={vendorModalFilter.locationFilter}
                  onChange={e => setVendorModalFilter(prev => ({
                    ...prev,
                    locationFilter: e.target.value
                  }))}
                  onKeyPress={e => {
                    if (e.key === 'Enter') {
                      saveSearchHistory(vendorModalFilter.locationFilter);
                    }
                  }}
                />
              </div>
            )}

            {/* Fix #4: Category Filter Dropdown — multi-select from vendimgcat */}
            {showVendorCategoryFilter && (
              <div className="vendor-modal-filter-dropdown">
                {vendorCategories.length === 0 ? (
                  <div style={{ padding: '8px 4px', fontSize: 12, color: '#666' }}>
                    No categories available
                  </div>
                ) : (
                  vendorCategories.map(cat => {
                    const isSelected = vendorModalFilter.categoryFilter.includes(cat.id);
                    return (
                      <button
                        key={cat.id}
                        className={`vendor-modal-category-option ${isSelected ? 'active' : ''}`}
                        onClick={() => {
                          setVendorModalFilter(prev => {
                            const next = isSelected
                              ? prev.categoryFilter.filter(id => id !== cat.id)
                              : [...prev.categoryFilter, cat.id];
                            return { ...prev, categoryFilter: next };
                          });
                        }}
                      >
                        <span>{cat.name}</span>
                        <span className="vendor-modal-category-checkbox">
                          {isSelected && (
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="white"
                              strokeWidth="4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}

            {/* Tabs — Fix #4: scrollable on small screens */}
            <div className="vendor-modal-tabs">
              {[
                { key: 'sponsored', label: 'Sponsored' },
                { key: 'recommended', label: 'Recommended' },
                { key: 'popular', label: 'Popular' },
                { key: 'following', label: 'Following' },
                { key: 'new', label: 'New' }
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`vendor-modal-tab ${vendorModalFilter.tab === tab.key ? 'active' : ''}`}
                  onClick={() => setVendorModalFilter(prev => ({ ...prev, tab: tab.key as any }))}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Vendor List */}
            <div
              ref={vendorModalScrollRef}
              className="vendor-modal-list"
              onScroll={handleVendorModalScroll}
            >
              {vendorModalVendors.map(vendor => {
                const vendorProducts = products.filter(p => p.vendor_id === vendor.vendor_id);
                const displayImage = vendor.cover_image || vendor.profile_image || 
                  (vendorProducts[0]?.images?.[0]) || '';
                const imageSrc = displayImage ? getThumbnailUrl(displayImage, 200, 70) : '';

                return (
                  <div
                    key={vendor.id}
                    className="vendor-modal-card"
                    onClick={() => {
                      setShowVendorModal(false);
                      handleVendorClick(vendor.vendor_id);
                    }}
                  >
                    <div className="vendor-modal-card-image">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={vendor.shop_name}
                          loading="lazy"
                          onError={e => {
                            const img = e.currentTarget;
                            if (img.dataset.fallbackDone === 'true') return;
                            img.dataset.fallbackDone = 'true';
                            if (displayImage) {
                              img.src = getOriginalUrl(displayImage);
                            }
                          }}
                        />
                      ) : (
                        <div className="vendor-modal-card-placeholder">
                          {vendor.shop_name.charAt(0)}
                        </div>
                      )}
                      {vendor.online_status && <div className="vendor-modal-online" />}
                    </div>
                    <div className="vendor-modal-card-info">
                      <div className="vendor-modal-card-name">
                        {vendor.shop_name}
                        {vendor.is_verified && (
                          <CheckCircle size={12} className="vendor-modal-verified" />
                        )}
                      </div>
                      {vendor.location && (
                        <div className="vendor-modal-card-location">
                          <MapPin size={10} /> {vendor.location}
                        </div>
                      )}
                      <div className="vendor-modal-card-stats">
                        <span><Star size={10} /> {vendor.rating.toFixed(1)}</span>
                        <span><Users size={10} /> {vendor.followers_count}</span>
                        <span><Package size={10} /> {vendor.total_products || 0}</span>
                      </div>
                    </div>
                    {vendor.hashtags && (
                      <div className="vendor-modal-card-hashtags">
                        {parseHashtags(vendor.hashtags).slice(0, 3).map(tag => (
                          <span key={tag} className="vendor-modal-hashtag">#{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {vendorModalLoading && (
                <div className="vendor-modal-loading">
                  <RefreshCw className="animate-spin" size={20} />
                  <span>Loading vendors...</span>
                </div>
              )}

              {!vendorModalLoading && vendorModalVendors.length === 0 && (
                <div className="vendor-modal-empty">
                  <Store size={40} />
                  <p>No vendors found</p>
                  <span>Try adjusting your filters</span>
                </div>
              )}
            </div>

            {/* Clear All Filters */}
            {(vendorModalFilter.locationFilter || vendorModalFilter.categoryFilter.length > 0 || vendorModalFilter.searchQuery) && (
              <div className="vendor-modal-clear-filters">
                <button onClick={clearVendorModalFilters}>
                  <X size={14} /> Clear All Filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* OVERLAYS */}
      {showMarketplaceOverlay && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1000,
            transition: 'opacity 0.3s ease'
          }}
          onClick={handleMarketplaceOverlayClick}
        />
      )}

      {showVendorShop && selectedVendorId && (
        <div
          ref={vendorShopRef}
          style={getVendorShopDimensions()}
          onClick={e => {
            e.stopPropagation();
            if (showProductDisplay) setShowProductDisplay(false);
          }}
        >
          <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <VendorShop
              vendorId={selectedVendorId}
              embedded={true}
              onClose={handleCancelStep}
              onProductClick={handleVendorShopProductClick}
              initialProductId={selectedProduct?.id || null}
              isBlurred={showProductDisplay}
              onToggleExpand={() => setVendorShopMaximized(!vendorShopMaximized)}
            />
          </div>
        </div>
      )}

      {showProductDisplay && selectedProduct && (
        <div
          ref={productDisplayRef}
          style={getProductDisplayDimensions()}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <ProductDisplay
              product={selectedProduct}
              vendor={vendorProfiles[selectedProduct.vendor_id] || null}
              currentImageIndex={0}
              onClose={handleBackToVendorShop}
              onNavigateToVendorShop={handleBackToVendorShop}
              embedded={true}
              onToggleExpand={() => setProductDisplayMaximized(!productDisplayMaximized)}
            />
          </div>
        </div>
      )}

      {(showVendorShop || showProductDisplay) && (
        <div
          style={{
            position: 'fixed',
            top: '6px',
            right: '6px',
            zIndex: 1004,
            display: 'flex',
            gap: '3px',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '3px',
              padding: '3px',
              borderRadius: '15px',
              boxShadow: '0 1px 5px rgba(0,0,0,0.2)',
              border: '0.5px solid rgba(0,0,0,0.1)'
            }}
          >
            {navStackIndex > 0 && (
              <button
                onClick={handleBack}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '14px',
                  backgroundColor: '#f5f5f5',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Go back"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            {navStackIndex < navStack.length - 1 && (
              <button
                onClick={handleForward}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '14px',
                  backgroundColor: '#f5f5f5',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Go forward"
              >
                <ArrowRight size={16} />
              </button>
            )}
            <button
              onClick={handleToggleMaximize}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                backgroundColor: '#f5f5f5',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={
                showProductDisplay
                  ? productDisplayMaximized
                    ? 'Minimize'
                    : 'Maximize'
                  : vendorShopMaximized
                  ? 'Minimize'
                  : 'Maximize'
              }
            >
              {showProductDisplay
                ? productDisplayMaximized
                  ? <Minimize2 size={14} />
                  : <Maximize2 size={14} />
                : vendorShopMaximized
                ? <Minimize2 size={14} />
                : <Maximize2 size={14} />}
            </button>
            <button
              onClick={handleCancelStep}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '14px',
                backgroundColor: showProductDisplay ? '#ff9500' : '#ff3b30',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={
                showProductDisplay
                  ? 'Close product (back to shop)'
                  : 'Close shop (back to marketplace)'
              }
            >
              {showProductDisplay ? <ArrowLeft size={16} /> : <X size={16} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Marketplace;